import {
    getArrivalTimeAsDayjs,
    getArrivalTimeAsSecondsSinceMidnight,
    getDepartureTime,
    getDepartureTimeAsDayjs,
    getDepartureTimeAsSecondsSinceMidnight,
    getDistanceInMetres,
    getNearestSegment,
    hasLogo,
    normaliseDate,
    publicStopFilter,
    SECONDS_IN_DAY,
    timingPtFilter,
    updateRouteDeltas
} from '../libs/routes-lib';
import {toHrsMinsSecs, toKmMs} from '../libs/formatLib';
import {ensureWpAtStop, toLineString} from '../libs/mapLib';
// eslint-disable-next-line
import util from 'util';
import {cloneDeep, find, findIndex, findLastIndex, forIn, intersection, isArray, last, pullAllBy, uniq} from 'lodash';
import dayjs from '../dayjs';
import {keyBy} from 'lodash/collection';
import {ulid} from 'ulid';
import {getCachedPath, snapToRoadHere} from '../libs/pathLib';
import {flatten, uniqBy} from 'lodash/array';

import log from 'loglevel';
import {values} from 'lodash/object';
import {dayjsToSecsSinceMidnight, TimeFilterType} from './timeFilter';
import {findLinkedSchoolStops, validateBellWindow} from './school';
import simplify from 'simplify-js';
import { detectDutyOverlaps } from '../utils/dutyUtils';

const logger = log.getLogger('BusRoute');

export const DATE_STRING = 'DD/MM/YYYY';
export const DATE_DAY_STRING = 'ddd, MMM D, YYYY';

const _addStopTimeFn = (stop, secs) => {
    stop.arriveSecs += secs;
    stop.departSecs += secs;
};

export const STOP_TYPES = {
    depot: 'Depot',
    nonpub: 'Non public',
    school: 'School',
    bus: 'Bus stop'
};

export const ROUTE_STATUSES = [
    {label: 'Draft', value: -1},
    {label: 'Approved', value: 0},
    {label: 'Published', value: 1}
];

export const ROUTE_STATUS = {[-1]: 'Draft', 0: 'Approved', 1: 'Published'};

export class Model {
    update(data) {
        if (data) {
            Object.assign(this, data);
        }
    }

    clone() {
        return {...this};
    }
}

const DWELL_TIME = 30;

const METRES_PER_SEC = 120 / 3.6;

const WP_OPTIMISE_RATE = 0.00001;

export class BusRoute extends Model {
    constructor(data) {
        super();
        this.routeId = ulid();
        this.scheduledAt = null; // Epoch. Will be set when the schedule is updated
        this.publishedAt = null; // Epoch. Will be set when the route is published to TfNSW
        this.author = null;
        this.routeNumber = null;
        this.routeName = null;
        this.routeType = null; // Regular, School
        this.driverShift = null;
        this.vehicleShift = null;
        this.vehicleTypes = [];
        this.vehicleTypeQuantities = {};
        this.waypoints = [];
        this.stops = []; // List of stops this route services
        this.stopTimes = []; // Base list of stopTimes for the route
        this.schedule = null; //
        // this.trips = {}; // {scheduleId: [Trip]}
        this.services = []; // {tripId, scheduleIds: []}
        this.startWpIdx = 0;
        this.endWpIdx = 0;
        this.startStopIdx = -1;
        this.endStopIdx = -1;
        this.colour = null;
        this.published = -1;
        // charter specific
        this.charter = false;
        this.startDepot = null;
        this.endDepot = null;
        this.passengerCount = 0;
        this.returnEnabled = false;
        this.dates = [];

        Object.assign(this, data);

        this.services = this.services?.map(trip => new Trip({...trip, route: this})) || [];
        this.services.sort((t1, t2) => (t1.stopTimes[0]?.departSecs) - (t2.stopTimes[0]?.departSecs))

        if (this.stopTimes && this.stopTimes.length) {
            this.stopTimes.forEach(st => {
                st.date = normaliseDate(st.date)?.startOf('day') || null;
            });
        } else if (this.stops) {
            this.stopTimes = cloneDeep(this.stops);
        }

        this.tripCount = this.services.reduce((prev, trip) => {
            return prev + (trip.scheduleIds?.length || 1);
        }, 0);

        if (data?.waypoints) {
            this.waypoints = data.waypoints.map(wp => new WayPoint(wp)).filter(wp => wp.isValid());
            this.endWpIdx = data.endWpIdx > -1 && data.endWpIdx < this.waypoints.length ? data.endWpIdx : this.waypoints.length - 1;
        }

        if(this.dates?.length) {
            this.dates = this.dates.map(date => normaliseDate(date)?.startOf('day') || null);
        }

        // this.calculateStartEnd({});
        // this.updateStopPoints();
        // this.calculateDistances();

        // if (!data.originalWaypoints) {
        //     this.originalWaypoints = data.waypoints;
        // }
        // if (!data.originalStops) {
        //     this.originalStops = data.stops;
        // }
        if (data?.scheduleId && !data.schedule) {
            delete data.scheduleId;
        }

        this.noTrips = !this.hasServices();

        // BUS-607, bad data... seconds since epoch was saved, instead of ms
        if (dayjs(this.createdAt).year() < 2000) {
            this.createdAt *= 1000;
        }
        if (dayjs(this.updatedAt).year() < 2000) {
            this.updatedAt *= 1000;
        }
    }
    
    isSchoolRoute() {
        return this.routeType === 'School' || this.services.some(trip => trip.routeType === 'School');
    }

    getEditor() {
        return this.lastEditor?.length ? this.lastEditor : this.author?.length ? this.author : 'Unknown';
    }

    setSchedules(allSchedules) {
        this.services.forEach(trip => {
            trip.schedules = trip.scheduleIds.map(sId => allSchedules[sId]).filter(s => !!s);
        });
    }

    clone(field, value) {
        const newRoute = new BusRoute(cloneDeep(this));
        if (field?.length) {
            newRoute[field] = value;
        }
        return newRoute;
    }

    shallowClone() {
        return new BusRoute({
            ...this,
            services: this.services.map(s => s.clone()),
            waypoints: this.waypoints.map(wp => ({...wp})),
            stops: this.stops.map(s => s.clone()),
            stopTimes: this.stopTimes.map(st => ({...st}))
        });
    }

    static create({routeId = '_', routeType = 'Regular', author = ''}) {
        return new BusRoute({
            routeId,
            routeNumber: '',
            routeName: '',
            routeDetails: '',
            waypoints: [],
            stopTimes: [],
            colour: '#007bff',
            routeType,
            author,
            createdAt: Date.now(),
            direction: 'Inbound',
            services: []
        });
    }

    addWayPoint(wp, idx) {
        let previousWP = this.waypoints[idx - 1];
        wp.distance = (previousWP?.distance || 0) + wp.distance;
        wp.delta = (previousWP?.delta || 0) + wp.time;
        this.waypoints.forEach((wp, i) => {
            if (i >= idx) {
                this.waypoints[i].delta += wp.delta;
                this.waypoints[i].distance += wp.distance;
            }
        });
        const waypoint = new WayPoint(wp);
        this.waypoints.splice(idx, 0, waypoint);
        return waypoint;
    }

    addPath(path, idx = this.waypoints.length - 1) {
        let previousWP = this.waypoints[idx];
        logger.debug(idx);
        logger.debug(this.waypoints.map(wp => wp.delta));
        logger.debug('PREVWP: ', previousWP);
        this.waypoints.forEach((wp, i) => {
            if (i > idx) {
                wp.delta += path.duration;
                wp.distance += path.distance;
            }
        });
        // const waypoint = new WayPoint(wp)
        path.waypoints.map(wp => {
            const waypoint = new WayPoint(wp);
            if (previousWP) {
                waypoint.distance += previousWP.distance;
                waypoint.delta += previousWP.delta;
            }
            return waypoint;
        }).forEach((wp, i) => this.waypoints.splice(idx + 1 + i, 0, wp));
    }

    getAvgSpeed(idx = this.waypoints.length - 1) {
        const lastWp = this.waypoints[idx];
        return lastWp?.delta > 0 ? (lastWp.distance / lastWp.delta) : 14; // default 50 km/hr
    }

    async addFirstStop(stop, tp = false) {
        const baseStop = {
            ...stop,
            stopTimeId: ulid(),
            timingPoint: tp,
            date: stop.date || this.charter?.dates?.[0] || null,
            arriveSecs: stop.arriveSecs || 0,
            departSecs: stop.departSecs || 0,
            wpIdx: 0
        };

        this.addStopAtIndex(baseStop, 0);
        const {lat, lon} = await snapToRoadHere(baseStop);
        this.waypoints = [{lat, lon, delta: 0, distance: 0, stopId: stop.stopId}];

        return true;
    }

    getWpIndexForStopTimeId(stopTimeId) {
        return this.waypoints.findIndex(wp => wp.stopTimeId === stopTimeId);
    }

    addFirstPt(pt) {
        this.waypoints = [{...pt, delta: 0, distance: 0}];
        return true;
    }


    getDriverRequirements() {
        const duration = this.getDuration();
        const trips  = {}
        this.services.forEach(trip => {
            trips[trip.tripId] = trip.getDriverRequirements();
        })
        return {duration, trips}
    }


    async insertWaypointsBetweenWaypoints(wpIdx1, wpIdx2, replace = false) {
        logger.debug('Looking up waypoints between %d and %d', wpIdx1, wpIdx2);
        if (replace) {
            this.cut(wpIdx1 + 1, wpIdx2 - 1);
        }
        const path = await getCachedPath('bus', this.waypoints[wpIdx1], this.waypoints[wpIdx2]);
        path.waypoints.forEach((wp, i) => this.addWayPoint(wp, wpIdx1 + 1 + i));
        return path;
    }

    async insertWaypointsBetweenStops(stop1, stop2, replace = false) {
        logger.debug('Looking up waypoints between %s and %s', stop1.stopName, stop2.stopName);
        if (replace) {
            this.cut(stop1.wpIdx + 1, stop2.wpIdx - 1);
        }
        const path = await getCachedPath('bus', stop1, stop2);
        path.waypoints.forEach((wp, i) => this.addWayPoint(wp, stop1.wpIdx + 1 + i));
        return path;
    }

    async appendPath(stop, startIdx = null, endIdx = null) {
        logger.debug('Appending path');

        // If no stops yet, add this as first stop
        if (!this.waypoints?.length && !this.stops?.length) {
            return this.addFirstPt(stop);
        }
        logger.debug('wps before append:', this.waypoints.length);
        logger.debug('WP deltas', this.waypoints.map(wp => wp.delta));

        // if (Number.isFinite(startIdx) && Number.isFinite(endIdx) && endIdx > startIdx) {
        //     this.cut(startIdx, endIdx)
        // }

        startIdx = (Number.isFinite(startIdx) && startIdx > -1) ? startIdx : (this.waypoints?.length ? this.waypoints.length - 1 : last(this.stops).wpIdx);

        let path, startPt = this.waypoints[startIdx];
        try {
            // If second stop, there will be no waypoints yet.
            path = await getCachedPath('bus', startPt, stop);
        } catch (e) {
            console.log(e, e);
        }

        if (this.waypoints.length) {
            // We can remove the first as it is the same as the last waypoint.
            path.waypoints.splice(0, 1);
        }
        logger.debug(path);

        // Add waypoints to the end
        this.addPath(path, startIdx);
        this.calculateDistances();
        return true;
    }

    async prependPath(stop) {
        logger.debug('Prepending path');

        // If no stops yet, add this as first stop
        if (!this.waypoints?.length && !this.stops?.length) {
            return this.addFirstPt(stop);
        }
        logger.debug('wps before append:', this.waypoints.length);
        logger.debug('WP deltas', this.waypoints.map(wp => wp.delta));

        let path, endPt = this.waypoints?.length ? this.waypoints[0] : this.stops[0];
        try {
            // If second stop, there will be no waypoints yet.
            path = await getCachedPath('bus', stop, endPt);
        } catch (e) {
            console.log(e, e);
        }

        if (this.waypoints.length) {
            // We can remove the first as it is the same as the last waypoint.
            path.waypoints.splice(-1, 1);
        }
        logger.debug(path);

        // Add waypoints to the end
        this.addPath(path, -1);
        this.calculateDistances();
        return true;
    }

    async insertPath(point, dragIdx, startWpIdx, endWpIdx = -1) {
        try {
            // If no stops yet, add this as first stop
            if (!this.waypoints?.length && !this.stops?.length) {
                return this.addFirstPt(point);
            }
            logger.debug('wps before append:', this.waypoints.length);
            logger.debug('WP deltas', this.waypoints.map(wp => wp.delta));

            // If second stop, there will be no waypoints yet.
            const startPath = await getCachedPath('bus', this.waypoints[startWpIdx], point);
            // We can remove the first as it is the same as the last waypoint.
            // startPath.waypoints.splice(0, 1);
            logger.debug(startPath);

            // Add waypoints to the start
            if (endWpIdx > -1) {
                // If second stop, there will be no waypoints yet.
                const endPath = await getCachedPath('bus', point, this.waypoints[endWpIdx]);
                // We can remove the first as it is the same as the last waypoint.
                // endPath.waypoints.splice(0, 1);
                logger.debug(endPath);

                this.cut(startWpIdx + 1, endWpIdx - 1);
                this.addPath(startPath, startWpIdx);
// this.cut(startWpIdx + startPath.waypoints.length, endWpIdx);

                // Add waypoints to the end
                this.addPath(endPath, startWpIdx + startPath.waypoints.length);
            }
            this.calculateDistances();
            return true;
        } catch (err) {
            logger.debug(err);
        }
    }

    async replacePath(path, startWpIdx, endWpIdx = -1) {
        try {
            if (!this.waypoints?.length || !this.stops?.length) {
                return;
            }

            logger.debug('wps before replace:', this.waypoints.length);

            if (endWpIdx > startWpIdx) {
                this.cut(startWpIdx + 1, endWpIdx, false);
            }
            this.addPath(path, startWpIdx);
            logger.debug('wps after replace:', this.waypoints.length);

            // Add waypoints to the end
            this.calculateDistances();
            return true;
        } catch (err) {
            logger.debug(err);
        }
    }

    async appendStop(stop, startIdx, allowSameStop = false) {
        logger.debug('Appending stop');

        // If no stops yet, add this as first stop
        if (!this.waypoints?.length && !this.stops?.filter(s => !s.placeholder).length) {
            return await this.addFirstStop(stop);
        }

        const realStops = this.stops?.filter(s => !s.placeholder);

        // Get the previous stop without filtering placeholders
        const prevStop = last(realStops);

        // Only check stopId for uniqueness, not coordinates
        if (stop.stopId === prevStop?.stopId && !stop.isReturnStop && !allowSameStop) {
            logger.warn('Trying to append to the same stop. Ignoring.');
            return true;
        }

        logger.debug('wps before append:', this.waypoints.length);
        logger.debug('WP deltas', this.waypoints.map(wp => wp.delta));

        let startPt = startIdx ? this.waypoints[startIdx] : this.waypoints.length ? last(this.waypoints) : last(this.stops);
        // If second stop, there will be no waypoints yet.
        let path = await getCachedPath('bus', startPt, stop);

        if (this.waypoints.length) {
            // We can remove the first as it is the same as the last waypoint.
            path.waypoints.splice(0, 1);
        }
        logger.debug(path);

        // Add waypoints to the end
        this.addPath(path);

        logger.debug('wps after append:', this.waypoints.length);

        logger.debug('WP deltas', this.waypoints.map(wp => wp.delta));

        const seconds = stop.arriveSecs || (prevStop?.departSecs || 0) + DWELL_TIME + path.duration; // Add 30 seconds for stopping time

        const baseStop = {
            ...stop,
            stopTimeId: ulid(),
            arriveSecs: seconds,
            departSecs: seconds,
            date: stop.date || prevStop?.date || this.charter?.dates?.[0] || null
        };

        // const arriveDate = date.startOf('day').add(seconds, 's')
        // const newStopSeconds = dayjsToSecsSinceMidnight(arriveDate);
        // baseStop.dateOffset = arriveDate.startOf('day').diff(date.startOf('day'), 'days');
        // baseStop.arriveSecs = newStopSeconds;
        // baseStop.departSecs = newStopSeconds;

        this.stops.push(baseStop);
        const {stopTimeId, stopId, arriveSecs, departSecs, date} = baseStop;
        const stopTime = {stopTimeId, stopId, arriveSecs, departSecs, date};
        this.stopTimes.push(stopTime);
        this.services.forEach(trip => {
            trip.stopTimes.push({
                ...stopTime,
                departSecs: (trip.stopTimes[trip.stopTimes.length - 1]?.departSecs || 0) + DWELL_TIME + path.duration,
                arriveSecs: (trip.stopTimes[trip.stopTimes.length - 1]?.departSecs || 0) + DWELL_TIME + path.duration
            });
            trip.stops.push({
                ...baseStop,
                departSecs: (trip.stopTimes[trip.stopTimes.length - 1]?.departSecs || 0),
                arriveSecs: (trip.stopTimes[trip.stopTimes.length - 1]?.departSecs || 0)
            });
        });

        // Ensure wp at stop
        this.calculateStartEnd({firstStop: this.stops[0], lastStop: baseStop, stops: this.stops, ensureStop: false});
        this.calculateDistances();
        return true;
    }

    async prependStop(stop) {
        logger.debug('Prepending stop');

        // If no stops yet, add this as first stop
        if (!this.waypoints?.length && !this.stops?.filter(s => !s.placeholder).length) {
            return await this.addFirstStop(stop);
        }

        if (stop.stopId === this.stops[0].stopId) {
            logger.debug('Trying to prepend from the same stop. Ignoring.');
            return true;
        }

        // Get the waypoints between lastStop and stop amd insert
        const path = await getCachedPath('bus', stop, this.waypoints[0]);
        path.waypoints.splice(-1, 1);

        // Add waypoints from ths start
        this.addPath(path, -1);
        // path.waypoints.forEach((wp, idx) => this.addWayPoint(wp, idx))

        // const insertedWaypoints = await this.insertWaypointsBetweenStops(stop, nextStop);
        let pathDuration = Math.round(path.duration);

        const baseStop = {
            ...stop,
            stopTimeId: ulid(),
            arriveSecs: this.stops[0].arriveSecs - pathDuration,
            departSecs: this.stops[0].departSecs - pathDuration,
            date: stop.date || this.stops[0].date || this.charter?.dates?.[0] || null
        };

        // if (stop.master) {
        //     delete baseStop.master
        //     baseStop.stopId = stop.stopId+'-m'
        //     baseStop.verified = 1
        // }

        this.stops.unshift(baseStop);
        const {stopTimeId, stopId, date} = baseStop;
        const stopTime = {
            stopTimeId, stopId, arriveSecs: this.stopTimes[0].arriveSecs - pathDuration,
            departSecs: this.stopTimes[0].departSecs - pathDuration,
            date
        };
        this.stopTimes.unshift(stopTime);

        this.services.forEach(trip => {
            const newSt = {...stopTime};
            newSt.arriveSecs = trip.stopTimes[0].arriveSecs - pathDuration;
            newSt.departSecs = trip.stopTimes[0].departSecs - pathDuration;
            trip.stopTimes.unshift(newSt);
            trip.stops.unshift({...baseStop});
        });
        // this.addStopAtIndex(baseStop, 0)

        // Ensure wp at stop
        this.calculateStartEnd({firstStop: baseStop, stops: this.stops, ensureStop: false});
        this.calculateDistances();
        return true;
    }

    async insertStopBetweenPoints(stop, startWpIdx, endWpIdx) {
        logger.debug('Inserting stop between points, ', startWpIdx, endWpIdx);
        this.calculateStartEnd({stops: this.stops});

        // Get the waypoints between prevStop and stop amd insert
        logger.debug('Looking up waypoints between wpIdx: %d and %s', startWpIdx, stop.stopName);
        let startPath = await getCachedPath('bus', this.waypoints[startWpIdx], stop);

        startPath.waypoints.splice(0, 1);

        logger.debug('Looking up waypoints between %s and wpIdx: %d', stop.stopName, endWpIdx);
        let endPath = await getCachedPath('drive', stop, this.waypoints[endWpIdx]);

        endPath.waypoints.splice(-1, 1);
        this.cut(startWpIdx + 1, endWpIdx - 1);

        // Add waypoints from ths start
        this.addPath(startPath, startWpIdx);
        // Add waypoints from ths start
        this.addPath(endPath, startWpIdx + startPath.waypoints?.length);

        this.calculateStartEnd({stops: this.stops, ensureStop: false});
        this.calculateDistances();

        await this.addStop(stop, 'add');

        // Ensure wp at stop
        this.calculateStartEnd({stops: this.stops, ensureStop: false});
        return true;
    }

    /**
     * Inserts a stop between two other stops, calculates the path between them and updates the stop times suing the duration of the new path
     * @param stop
     * @param prevStop
     * @param nextStop
     * @returns {Promise<boolean>}
     */
    async insertStopBetweenStops(stop, prevStop, nextStop) {
        logger.debug('Inserting stop');
        this.waypoints.splice(prevStop.wpIdx + 1, nextStop.wpIdx - prevStop.wpIdx);
        this.calculateStartEnd({stops: this.stops});

        const prevStopIdx = this.stops.findIndex(s => s.stopTimeId === prevStop.stopTimeId);
        const nextStopIdx = this.stops.findIndex(s => s.stopTimeId === nextStop.stopTimeId);

        // Get the waypoints between prevStop and stop amd insert
        logger.debug('Looking up waypoints between %s and %s', prevStop.stopName, stop.stopName);
        let leg1 = await getCachedPath('bus', prevStop, stop);
        leg1.waypoints.splice(0, 1);
        let newDelta = Math.round(leg1.duration);
        
        // Add waypoints from leg1
        this.addPath(leg1, prevStop.wpIdx);

        logger.debug('Looking up waypoints between %s and %s', stop.stopName, nextStop.stopName);
        let leg2 = await getCachedPath('bus', stop, nextStop);
        leg2.waypoints.splice(0, 1);
        newDelta += Math.round(leg2.duration);

        // Calculate the time difference between old and new paths
        const oldDuration = nextStop.arriveSecs - prevStop.departSecs;
        const additionalSecs = newDelta - oldDuration;

        // Add waypoints from leg2
        this.addPath(leg2, prevStop.wpIdx + leg1.waypoints.length);

        this.services.forEach(trip => {
            trip.stopTimes.forEach((st, idx) => idx > prevStopIdx && _addStopTimeFn(st, additionalSecs));
            trip.stops.forEach((st, idx) => idx > prevStopIdx && _addStopTimeFn(st, additionalSecs));
        });
        this.stops.forEach((s, idx) => idx > prevStopIdx && _addStopTimeFn(s, additionalSecs));
        this.stopTimes.forEach((s, idx) => idx > prevStopIdx && _addStopTimeFn(s, additionalSecs));

        logger.debug(this.waypoints.map(wp => wp.delta));
        const seconds = prevStop.departSecs + leg1.duration;
        logger.debug('wps: %d, prevStop.wpIdx: %d, prevWp delta: %d, start duration: %d, Stop Delta: ', this.waypoints.length, prevStop.wpIdx, this.waypoints[prevStop.wpIdx].delta, leg1.duration, seconds);
        
        const baseStop = {
            ...stop,
            stopTimeId: ulid(),
            arriveSecs: seconds,
            departSecs: seconds,
        };

        this.stops.splice(nextStopIdx, 0, baseStop);
        const {stopTimeId, stopId, arriveSecs, departSecs, date} = baseStop;
        const stopTime = {stopTimeId, stopId, arriveSecs, departSecs, date};
        this.stopTimes.splice(nextStopIdx, 0, stopTime);

        this.services.forEach(trip => {
            trip.stopTimes.splice(nextStopIdx, 0, {
                ...stopTime,
                arriveSecs: arriveSecs,
                departSecs: departSecs,
            });
            trip.stops.splice(nextStopIdx, 0, {
                ...baseStop,
                arriveSecs: arriveSecs,
                departSecs: departSecs,
            });
        });

        // Ensure wp at stop
        this.calculateStartEnd({stops: this.stops, ensureStop: false});
        this.calculateDistances();
        return true;
    }

    async insertStop(stop) {
        logger.debug('Inserting stop...');
        this.calculateStartEnd({stops: this.stops});

        logger.debug('Getting nearest segment...');
        const segment = getNearestSegment(stop, this.waypoints, {maxDistFrom: 2000})?.segment;
        logger.debug('Segment: ', segment);
        if (segment) {
            let prevStop = this.getStopBeforeWpIndex(segment.startIdx, this.stops);
            let nextStop = this.getStopAfterWpIndex(segment.startIdx, this.stops);
            if (prevStop && nextStop) {
                return await this.insertStopBetweenStops(stop, prevStop, nextStop);
            }
        }
    }

    /**
     * Rebuilds all waypoints between stops and checkpoints
     * @param {Object} opts - Options object
     * @param {number} opts.arriveSecs - The arrive time of the route
     * @param {number} opts.departSecs - The depart time of the route
     * @returns {BusRoute} The rebuilt route
     */
    async rebuildWaypoints({arriveSecs, departSecs} = {}) {
        if (!this.stops?.length) {
            return [];
        }

        const defaultArriveSecs = arriveSecs || this.stopTimes[0].arriveSecs;
        const defaultDepartSecs = departSecs || this.stopTimes[0].departSecs;

        // Reset waypoints array
        this.waypoints = [];
        const stops = this.stops.map(s => {
            delete s.wpIdx;
            delete s.arriveSecs;
            delete s.departSecs;
            return s;
        });
        this.stops = [];
        this.stopTimes = [];
        this.services.forEach(trip => {
            trip.stopTimes = [];
            trip.stops = [];
        });

        // Iterate through ordered points
        console.log('[BusRoute] rebuildWaypoints processing stops', {
            returnEnabled: this.returnEnabled,
            stopsCount: stops.length,
            stops: stops.map((s, idx) => ({
                idx,
                stopTimeId: s.stopTimeId,
                stopName: s.stopName || s.stopId,
                isReturnStop: !!s.isReturnStop,
                immutable: !!s.immutable
            }))
        });

        for (let i = 0; i < stops.length; i++) {
            const point = stops[i];
            console.log(`[BusRoute] rebuildWaypoints adding stop ${i}/${stops.length - 1}`, {
                stopTimeId: point.stopTimeId,
                stopName: point.stopName || point.stopId,
                isReturnStop: !!point.isReturnStop,
                immutable: !!point.immutable
            });
            await this.appendStop(point);

            // Set default start times for first stop and services before adding the next stop
            if (i === 0) {
                this.services[0].stopTimes[0].arriveSecs = defaultArriveSecs;
                this.services[0].stopTimes[0].departSecs = defaultDepartSecs;
                this.updateStops();
            }
        }
        this.calculateDistances();

        return this;
    }

    async addStop(stop, method, tp = false) {
        logger.debug('Adding stop...', method);

        // If no stops yet, add this as first stop
        if (!this.waypoints?.length && !this.stops?.filter(s => !s.placeholder).length) {
            return await this.addFirstStop(stop, tp);
        }

        this.calculateStartEnd({stops: this.stops});

        if (method === 'append' || (method === 'add' && this.charter)) {
            if(this.returnEnabled) {
                // if only one stop, add as return and middle
                if(this.stops.length === 1) {
                    await this.appendStop(stop);
                    return await this.appendStop(this.stops[0]);
                }
                const prevStop = this.stops[this.stops.length-2];
                const nextStop = this.stops[this.stops.length-1];
                return await this.insertStopBetweenStops(stop, prevStop, nextStop);
            }
            return await this.appendStop(stop);
        }
        if (method === 'prepend') {
            return await this.prependStop(stop);
        }

        logger.debug('Getting nearest segment...');
        const segment = getNearestSegment(stop, this.waypoints, {maxDistFrom: 2000})?.segment;
        logger.debug('Segment: ', segment);
        if (segment) {
            let prevStop = this.getStopBeforeWpIndex(segment.startIdx, this.stops);
            let nextStop = this.getStopAfterWpIndex(segment.startIdx, this.stops);

            if ((!method || method === 'add') && segment.dist < 100) {

                let timeToCoverDistance = 0;
                if (!prevStop) {
                    // New start of route...
                    stop.arriveSecs = 0;
                    stop.departSecs = 0;
                    stop.distance = 0;
                } else {

                    let speed = 14;
                    if (this.stops?.length > 1) {
                        speed = this.getAvgSpdBetweenStops(this.getStartStop(this.stops), this.getEndStop(this.stops));
                    }
                    if (prevStop && nextStop) {
                        speed = this.getAvgSpdBetweenStops(prevStop, nextStop) || speed;
                    }
                    let distance = this.getDistanceBetweenWpIdxs(prevStop.wpIdx, segment.startIdx) + segment.distToStart;
                    timeToCoverDistance = Math.round(distance / speed);
                    stop.distance = prevStop.distance + distance;
                }
                const stopTimeId = ulid();

                const stopTime = {
                    stopTimeId,
                    timingPoint: tp,
                    stopId: stop.stopId,
                };
                this.stops = this.stops || [];
                let idx = 0;
                if (this.stops.length) {
                    idx = this.findStopIndex(stop.distance);
                }
                if (idx > -1) {
                    this.addStopAtIndex({...stop, ...stopTime}, idx, timeToCoverDistance);
                } else {
                    this.stops.push({
                        ...stop, arriveSecs: prevStop.departSecs + timeToCoverDistance,
                        departSecs: prevStop.departSecs + timeToCoverDistance, ...stopTime
                    });
                    this.stopTimes.push({
                        ...stopTime, arriveSecs: prevStop.departSecs + timeToCoverDistance,
                        departSecs: prevStop.departSecs + timeToCoverDistance
                    });
                    this.services.forEach(trip => {
                        trip.stopTimes.push({
                            ...stopTime, arriveSecs: last(trip.stopTimes).departSecs + timeToCoverDistance,
                            departSecs: last(trip.stopTimes).departSecs + timeToCoverDistance
                        });
                        if (trip.stops?.length) {
                            trip.stops.push({
                                ...stop, ...stopTime,
                                arriveSecs: last(trip.stops).departSecs + timeToCoverDistance,
                                departSecs: last(trip.stopTimes).departSecs
                            });
                        }
                    });

                }
                if (prevStop) {
                    ensureWpAtStop(stop, this, prevStop.wpIdx);
                }
                this.calculateDistances();
                return true;
            }

            logger.debug('Distance to route is more than 20m... Going to calculate new route...');
            if (prevStop && nextStop && method === 'insert') {
                return await this.insertStopBetweenStops(stop, prevStop, nextStop);
            }
        } else {
            if (!this.stops?.filter(s => !s.placeholder).length) {
                logger.debug('No segment can be found. Assuming first stop.');
                return await this.addFirstStop(stop);
            }
            // return await this.appendStop(stop);
        }
    }

    removeStop(stop) {
        logger.debug('Removing stop:', stop.stopName);
        const currentIdx = this.findIndexForStopTimeId(stop.stopTimeId);
        if(currentIdx === -1) {
            console.log('Stop time id not found in any service');
            return;
        }
        this.removeStopAtIndex(currentIdx);
        if (stop.wpIdx >= 0 && stop.wpIdx < this.waypoints.length) {
            delete this.waypoints[stop.wpIdx].stopId;
            delete this.waypoints[stop.wpIdx].stopTimeId;
        }
        if(this.stopTimes.length === 0) {
            this.waypoints.length = 0;
        }
        logger.debug('Removed stop. Remaining: ', this.stops);
    }

    updateStops() {
        this.services.forEach(trip => {
            trip.stopTimes.forEach(stopTime => {
                const tripBaseStop = trip.stops.find(s => s.stopTimeId === stopTime.stopTimeId);
                if (tripBaseStop) {
                    tripBaseStop.arriveSecs = stopTime.arriveSecs;
                    tripBaseStop.departSecs = stopTime.departSecs;
                    tripBaseStop.date = stopTime.date;
                }
                const baseStop = this.stops.find(s => s.stopTimeId === stopTime.stopTimeId);
                if (baseStop) {
                    baseStop.arriveSecs = stopTime.arriveSecs;
                    baseStop.departSecs = stopTime.departSecs;
                    baseStop.date = stopTime.date;
                }
                const routeStopTime = this.stopTimes.find(s => s.stopTimeId === stopTime.stopTimeId);
                if (routeStopTime) {
                    routeStopTime.arriveSecs = stopTime.arriveSecs;
                    routeStopTime.departSecs = stopTime.departSecs;
                    routeStopTime.date = stopTime.date;
                }
            });
        });
    }

    removeStopAtIndex(idx) {
        const stop = this.stops[idx];
        this.stops.splice(idx, 1);
        this.stopTimes.splice(idx, 1);
        this.services.forEach(trip => {
            trip.stopTimes.splice(idx, 1);
            trip.stops.splice(idx, 1);
        });

        if (stop.wpIdx >= 0 && stop.wpIdx < this.waypoints.length) {
            delete this.waypoints[stop.wpIdx].stopId;
            delete this.waypoints[stop.wpIdx].stopTimeId;
        }
        if(this.stopTimes.length === 0) {
            this.waypoints.length = 0;
        }
    }

    addStopAtIndex(stop, idx, timeToCoverDistance = 0) {
        logger.debug('Adding stop:', stop.stopName, 'at', idx);
        const {stopTimeId, stopId, date} = stop;
        const stopTime = {stopTimeId, stopId, date};
        this.stops = this.stops || [];
        this.stopTimes = this.stopTimes || [];
        if (idx === 0) {
            this.stops.splice(idx, 0, {...stop, arriveSecs: 0, departSecs: 0});
            this.stopTimes.splice(idx, 0, {...stopTime, arriveSecs: 0, departSecs: 0});
            this.services = this.services || [];
            this.services.forEach(trip => {
                trip.stopTimes.splice(idx, 0, {...stopTime, arriveSecs: 0, departSecs: 0});
                trip.stops.splice(idx, 0, {...stop, arriveSecs: 0, departSecs: 0});
            });
            return;
        }
        this.stops.splice(idx, 0, {
            ...stop, arriveSecs: (this.stops[idx - 1]?.arriveSecs || 0) + timeToCoverDistance,
            departSecs: (this.stops[idx - 1]?.departSecs || 0) + timeToCoverDistance
        });
        this.stopTimes.splice(idx, 0, {
            ...stopTime, arriveSecs: (this.stopTimes[idx - 1]?.arriveSecs || 0) + timeToCoverDistance,
            departSecs: (this.stopTimes[idx - 1]?.departSecs || 0) + timeToCoverDistance
        });
        this.services = this.services || [];
        this.services.forEach(trip => {
            trip.stopTimes.splice(idx, 0, {
                ...stopTime, arriveSecs: (trip.stopTimes[idx - 1]?.arriveSecs || 0) + timeToCoverDistance,
                departSecs: (trip.stopTimes[idx - 1]?.departSecs || 0) + timeToCoverDistance
            });
            trip.stops.splice(idx, 0, {
                ...stop, arriveSecs: (this.stopTimes[idx - 1]?.arriveSecs || 0) + timeToCoverDistance,
                departSecs: (this.stopTimes[idx - 1]?.departSecs || 0) + timeToCoverDistance
            });
        });
    }

    setBaseStops(stopsById, stopTimes = this.stopTimes, opts = {
        timingPointsOnly: false,
        excludeLinkedStops: false,
        excludeCalculateStartEnd: false,
        // excludeTransfers: false,
        removeStops: true,
        operator: null
    }) {

        // logger.debug('Setting base stops for route %s: ', this.routeNumber, stopTimes.map(st => `${st.delta}`));
        this.stopTimes = stopTimes;
        const _stops = [];
        stopTimes.forEach((stopTime, idx) => {
            const stop = stopsById[stopTime.stopId];
            if (stop) {
                const baseStop = new BaseStop({...stop, ...stopTime, routeId: this.routeId});
                if (baseStop.master) {
                    baseStop.verified = 1;
                    baseStop.stopId = ulid();
                    delete baseStop.master;
                }
                if (!opts.excludeLinkedStops) {
                    baseStop.setLinkedStops(stopsById, true);
                }
                _stops.push(baseStop);
            } else {
                // Removing the following code as it could cause destructive changes to the routes in an event like a network issue.
                // if (opts.removeStops) {
                //     this.services.forEach(trip => trip.stopTimes.splice(idx, 1));
                // }
                console.log('No stop with ID: %s @ idx: %d for route: %s', stopTime.stopId, idx, this.routeNumber);
                // Object.keys(this.trips).forEach(scheduleId => this.trips[scheduleId] = this.trips[scheduleId].stopTimes.splice(idx, 1));
            }
        });
        this.stops = _stops;
        // if (this.stops.length) {
        //     this.stops[0].arriveSecs = this.stops[0].arriveSecs || 0
        //     this.stops[0].departSecs = this.stops[0].departSecs || 0
        //     this.stops.forEach(stop => {
        //         stop.departTime = getDepartureTimeAsDayjs(this, stop)
        //         stop.arriveSecs = stop.departTime.hour();
        //         stop.departMin = stop.departTime.minute();
        //         stop.departSec = stop.departTime.second();
        //     })
        //
        // }

        if (this.services?.length) {
            this.services.forEach(trip => {
                const _stops = [];
                trip.stopTimes.forEach(stopTime => {
                    const stop = stopsById[stopTime.stopId];
                    if (stop) {
                        const baseStop = new BaseStop({...stop, ...stopTime});
                        if (baseStop.master) {
                            baseStop.verified = 1;
                            baseStop.stopId = ulid();
                            delete baseStop.master;
                        }
                        if (!opts.excludeLinkedStops) {
                            baseStop.setLinkedStops(stopsById, true);
                        }
                        (!opts.timingPointsOnly || baseStop.timingPoint) && _stops.push(baseStop);
                        // if (!opts.excludeTransfers) {
                        //     const txFromRoute = find(stop.routes, r=> r.routeId === this.routeId)
                        //     const txFromTrip = txFromRoute ? txFromRoute.passingTimes[trip.tripId] : null
                        //     const txs = find(txFromTrip.pts, pt => pt.stopTimeId === stopTime.stopTimeId)
                        //     if(txs?.length) {
                        //         stopTime.transfersTo = txs.map(tx => ({
                        //             routeId: tx.toRouteId,
                        //             routeNumber: tx.toRouteNumber,
                        //             colour: tx.toRouteColour
                        //         }))
                        //     }
                        // }
                    } else {
                        console.log('No stop %s for route: %s', stopTime.stopId, this.routeNumber);
                    }
                });
                trip.stops = _stops;
            });
        }
        if (!opts.excludeCalculateStartEnd) {
            this.calculateStartEnd({firstStop: _stops[0], lastStop: last(_stops)});
        }
        this.calculateDistances();
        // logger.debug('Base stops: ', _stops.map(st => `${st.departHour}:${st.departMin}`));
        return _stops;
    }

    setTransfers(transfers, routes = {}, validOnly) {
        transfers = (Array.isArray(transfers) ? transfers : values(transfers)).filter(t => !validOnly || !t.invalid);
        this.services.forEach(trip => {
            trip.transfersTo = transfers.filter(tx => {
                const toRoute = routes?.[tx.toRouteId];
                const toTrip = find(toRoute?.services, {tripId: tx.toTripId});
                return tx.fromTripId === trip.tripId && intersection(trip.scheduleIds, toTrip?.scheduleIds).length;
            }).filter(tx => !!tx);
            trip.transfersFrom = transfers.filter(tx => {
                const fromRoute = routes?.[tx.fromRouteId];
                const fromTrip = find(fromRoute?.services, {tripId: tx.fromTripId});
                return tx.toTripId === trip.tripId && intersection(trip.scheduleIds, fromTrip?.scheduleIds).length;
            }).filter(tx => !!tx);
            trip.stopTimes.forEach(st => {
                st.transfersTo = trip.transfersTo
                    .filter(tx => tx.fromStopId === st.stopId && tx.fromStopTimeId === st.stopTimeId)
                    .map(tx => routes?.[tx.toRouteId] ? {
                        routeId: routes[tx.toRouteId].routeId,
                        routeNumber: routes[tx.toRouteId].routeNumber,
                        routeName: routes[tx.toRouteId].routeName,
                        colour: routes[tx.toRouteId].colour
                    } : {
                        routeId: tx.toRouteId,
                        routeNumber: tx.toRouteNumber,
                        colour: tx.toRouteColour,
                        ...tx
                    })
                    .filter(tx => !!tx);
                st.transfersFrom = trip.transfersFrom
                    .filter(tx => tx.toStopId === st.stopId && tx.toStopTimeId === st.stopTimeId)
                    .map(tx => routes?.[tx.fromRouteId] ? {
                        routeId: routes[tx.fromRouteId].routeId,
                        routeNumber: routes[tx.fromRouteId].routeNumber,
                        routeName: routes[tx.fromRouteId].routeName,
                        colour: routes[tx.fromRouteId].colour
                    } : {
                        routeId: tx.fromRouteId,
                        routeNumber: tx.fromRouteNumber,
                        colour: tx.fromRouteColour,
                        ...tx
                    })
                    .filter(tx => !!tx);
            });
        });
    }

    /**
     * Assumes setBaseStops has been called
     */
    createTransfers({transferWindow, defaultWindow, allStops} = {}) {
        let transfersForRoute = [];
        this.services.forEach(trip => {
            // Hack to ensure the routeId is in the trip
            trip.routeId = trip.routeId || this.routeId;
            trip.stops.filter(stop => transferWindow || defaultWindow || stop.transferWindow).forEach(stop => {
                console.log('Stop: ', stop.stopName);
                const transfers = stop.createTransfers({
                    transferWindow,
                    defaultWindow,
                    arriveSecs: stop.arriveSecs,
                    fromStopId: stop.stopId,
                    fromStopTimeId: stop.stopTimeId,
                    dropOffService: trip
                });
                const transfersByRoute = transfers.map(t => t.toRouteId + '_' + t.toTripId);
                transfersForRoute = transfersForRoute.concat(transfers);
                (stop.linkedStops || [])
                    .filter(linkedStop => linkedStop?.stop?.stopName)
                    .forEach(linkedStop => {
                        console.log('Linked stop: ', linkedStop.stop.stopName, linkedStop.distance, linkedStop.duration, linkedStop.waypoints?.length);
                        // const transfers = linkedStop.stop.createTransfers({
                        //     transferWindow: stop.transferWindow,
                        //     defaultWindow,
                        //     fromStopId: stop.stopId,
                        //     fromStopTimeId: stop.stopTimeId,
                        //     dropOffService: trip,
                        //     waypoints: linkedStop.waypoints,
                        //     distance: linkedStop.distance,
                        //     duration: linkedStop.duration
                        // });
                        // transfersForRoute = transfersForRoute.concat(transfers);
                        let transfers = allStops[linkedStop.stopId].createTransfers({
                            routeFilterFn: (routeId, tripId) => !transfersByRoute.includes(routeId + '_' + tripId),
                            transferWindow: transferWindow || stop.transferWindow,
                            arriveSecs: stop.arriveSecs,
                            defaultWindow,
                            fromStopId: stop.stopId,
                            fromStopTimeId: stop.stopTimeId,
                            dropOffService: trip,
                            waypoints: linkedStop.waypoints,
                            distance: linkedStop.distance,
                            duration: linkedStop.duration
                        });
                        transfersForRoute = transfersForRoute.concat(transfers);
                        console.log('Linked Transfers: ', transfers?.length);
                    });

                // find similar transfers and remove ones that aren't the shortest distance
                transfersForRoute = transfersForRoute.reduce((prev, transfer) => {
                    const existingTransfer = prev.find(t => t.fromRouteId === transfer.fromRouteId && t.fromTripId === transfer.fromTripId && t.toRouteId === transfer.toRouteId && t.toTripId === transfer.toTripId);
                    if (existingTransfer) {
                        if (existingTransfer.distance && (!transfer.distance || transfer.distance < existingTransfer.distance)) {
                            prev.splice(prev.indexOf(existingTransfer), 1, transfer);
                        }
                    } else {
                        prev.push(transfer);
                    }
                    return prev;
                }, []);
            });
        });
        return transfersForRoute;
    }

    findIndexForStopTimeId(stopTimeId) {
        let currentIdx = this.stopTimes.findIndex(s => s.stopTimeId === stopTimeId);
        if(currentIdx === -1) {
            // go through each service and find the stop time id
            for(const trip of this.services) {
                const tripIdx = trip.stopTimes.findIndex(s => s.stopTimeId === stopTimeId);
                if(tripIdx > -1) {
                    currentIdx = tripIdx;
                    break;
                }
            }
        }
        return currentIdx;
    }

    /**
     *
     * @param stop Complete stop expected ({...stop, ...stopTime})
     * @param sequence index of stop using base 1
     */
    setStopSequence(stop, sequence) {
        const currentIdx = this.findIndexForStopTimeId(stop.stopTimeId);
        if(currentIdx === -1) {
            console.log('Stop time id not found in any service');
            return;
        }
        const idx = sequence - 1;
        this.removeStopAtIndex(currentIdx);
        this.addStopAtIndex(stop, idx);
    }

    insertTrip(trip, idx = -1) {
        const existingTrip = this.services.find(t => t.isSameTrip(trip));
        if (existingTrip) {
            existingTrip.tripId += '|' + trip.tripId;
            existingTrip.scheduleIds = uniq([...existingTrip.scheduleIds, ...trip.scheduleIds]);
            existingTrip.dates = uniq([...existingTrip.dates, ...trip.dates]);
            return existingTrip;
        } else {
            this.services.splice(idx, 0, trip);
            if (this.services.length === 1) {
                this.stopTimes = [...trip.stopTimes];
            }
        }
        return trip;
    }

    addTrip() {
        this.services = this.services || [];
        let trip;
        if (this.services.length) {
            const copyOfLastTrip = cloneDeep(last(this.services));
            delete copyOfLastTrip.transfersFrom;
            delete copyOfLastTrip.transfersTo;
            trip = new Trip({
                ...copyOfLastTrip,
                tripId: null,
            });
            if (trip.stopTimes?.length) {
                trip.stopTimes.forEach(st => {
                    delete st.transfersFrom;
                    delete st.transfersTo;
                });
                trip.setStartTime(trip.stopTimes[0].arriveSecs + 3600);
            }
        } else {
            trip = new Trip({stopTimes: cloneDeep(this.stopTimes), routeId: this.routeId, stops: []});
        }
        this.services.push(trip);
        return trip;
    }

    removeTrip(tripId) {
        const tripIdx = this.services.findIndex(trip => trip.tripId === tripId);
        const trip = this.services[tripIdx];
        if (!trip) {
            return;
        }
        // trip.scheduleIds.splice(trip.scheduleIds.findIndex(sId => sId === scheduleId), 1)
        // if (!trip.scheduleIds.length) {
        this.services.splice(tripIdx, 1);
        // }
    }

    getTrip(tripId) {
        return this.services.find(trip => trip.tripId === tripId);
    }

    includesSchedule(scheduleId) {
        return this.getTripCountForSchedule(scheduleId) > 0;
    }

    getTripCountForSchedule(scheduleId) {
        return this.getTripsForSchedule(scheduleId).length;
    }

    getTripsForSchedule(scheduleId) {
        return this.services.reduce((prev, trip) => {
            if (trip.scheduleIds?.includes(scheduleId)) {
                prev.push(trip);
            }
            return prev;
        }, []);
    }

    getTripsForDate(date = dayjs()) {
        return this.services.reduce((prev, trip) => {
            if (trip.isRunning(date)) {
                prev.push(trip);
            }
            return prev;
        }, []);
    }

    getCharterDates(schedulesById) {
        return uniq(this.services.reduce((prev, trip) => {
            if (trip.dates?.length) {
                prev = prev.concat(trip.dates.map(date => date.format(DATE_DAY_STRING)));
            }
            if (trip.scheduleIds?.length) {
                prev = prev.concat(trip.scheduleIds.map(sId => schedulesById[sId].scheduleName));
            }
            return prev;
        }, []));
    }

    hasServices() {
        return this.services && !!this.getAnyService();
    }

    getAnyService() {
        return this.services[0];
    }

    isObsolete(schedulesById, date = dayjs()) {
        return !this.hasServices() || this.services.every(trip => trip.scheduleIds.every(sId => !schedulesById[sId] || schedulesById[sId].isObsolete(date)));
    }

    isFuture(schedulesById, date = dayjs()) {
        return this.hasServices() && this.services.every(trip => trip.scheduleIds.every(sId => schedulesById[sId]?.isFuture(date)));
    }

    isRunning(date = dayjs(), schedulesById) {
        return this.hasServices() && this.services.some(trip => trip.isRunning(date, schedulesById));
    }

    getRunningTrips(date = dayjs(), schedulesById) {
        return this.hasServices() && this.services.filter(trip => trip.isRunning(date, schedulesById));
    }

    someObsolete(schedulesById, date = dayjs()) {
        return !this.hasServices() || this.services.some(trip => trip.hasObsoleteCalendar(schedulesById));
    }

    findStopIndex(distance) {
        if (this.stops && this.stops.length) {
            return this.stops.findIndex(stop => {
                return stop.distance > distance;
            });
        }
        return -1;
    }

    getTotalDistance() {
        return (this.getDistance() || 0) + (this.startDepotDistance || 0) + (this.endDepotDistance || 0) + (this.returnDistance || 0);
    }

    getTotalDuration() {
        return this.services.reduce((prev, trip) => {
            if(!trip.getTotalDuration) {
                trip = new Trip(trip);
            }
            return prev + trip.getTotalDuration();
        }, 0);
    }

    calculateDistances({setDistance = true, setDuration = true} = {}) {
        this.waypoints.forEach((wp, idx) => {
            if (idx === 0) {
                wp.distance = 0;
                wp.delta = 0;
                return;
            }
            const prevWp = this.waypoints[idx - 1];
            const dist = getDistanceInMetres(prevWp, wp);
            wp.distance = prevWp.distance + dist;
        });

        if (this.stops?.length) {
            this.stops.forEach((stop, idx) => {
                if (stop.nearestSegment && stop.nearestSegment.startIdx > -1) {
                    const wp = this.waypoints[stop.nearestSegment.startIdx];
                    stop.distance = wp?.distance + stop.nearestSegment.distToStart;
                    
                    // Gracefully handle case where stopTimes array might be out of sync
                    if (this.stopTimes?.[idx]) {
                        this.stopTimes[idx].distance = stop.distance;
                    } else {
                        console.log('[DEBUG]: stopTimes array might be out of sync: ', stop, idx);
                    }
                    
                    this.services.forEach(trip => {
                        const tripStop = trip.stops?.[idx];
                        if(tripStop) {
                            tripStop.distance = stop.distance;
                        }
                        
                        // Gracefully handle case where trip stopTimes array might be out of sync
                        if (trip.stopTimes?.[idx]) {
                            trip.stopTimes[idx].distance = stop.distance;
                        } else {
                            console.log('[DEBUG]: trip stopTimes array might be out of sync: ', tripStop, idx);
                        }
                    });
                }
            });
        }

        updateRouteDeltas(0, this);

        const lastWp = last(this.waypoints);
        if(setDistance) {
            this.distance = lastWp?.distance;
        }
        if(setDuration) {
            this.duration = lastWp?.delta;
        }

    }

    calculateStartEnd({
                          firstStop = this.firstStop,
                          lastStop = this.lastStop,
                          checkDirection = false,
                          shortestRoute = false,
                          ensureStop = true,
                          stops = this.getPublicStops(),
                          trim = false
                      }) {
        const log = false;//this.routeNumber === '572'
        log && logger.debug('Stop count for %s calculating start/end: %d ', this.routeNumber, stops.length);
        log && logger.debug('WP count for %s calculating start/end: %d ', this.routeNumber, this.waypoints.length);
        if (stops?.length < 2) {
            log && logger.debug('not enough stops.');
            return;
        }

        if (!firstStop || !lastStop) {
            log && logger.debug('Using first/last public stops.');
            firstStop = firstStop || stops[0];
            lastStop = lastStop || stops[stops.length - 1];
            log && logger.debug('1st stop', firstStop.stopName, 'last stop', lastStop.stopName);
        }

        this.firstStop = firstStop;
        this.lastStop = lastStop;
        let time = Date.now();

        if (firstStop && lastStop) {
            log && logger.debug(this.routeNumber, 'checking start/stop idx', firstStop.stopName, 'to', lastStop.stopName, 'shortest route', shortestRoute);

            let startStopIdx = firstStop.sequence && firstStop.stopTimeId ? stops.findIndex(s => s.stopTimeId === firstStop.stopTimeId) : -1,
                endStopIdx = lastStop.sequence && lastStop.stopTimeId ? stops.findIndex(s => s.stopTimeId === lastStop.stopTimeId) : -1;
            if (startStopIdx < 0 || endStopIdx < 0) {
                let startStopIdxs = stops.reduce((prev, stop, idx) => {
                    prev = prev || [];
                    if (stop.stopId === firstStop.stopId) {
                        prev.push(idx);
                    }
                    return prev;
                }, []);
                let endStopIdxs = stops.reduce((prev, stop, idx) => {
                    prev = prev || [];
                    if (stop.stopId === lastStop.stopId) {
                        prev.push(idx);
                    }
                    return prev;
                }, []);

                log && logger.debug('startStopIdxs', startStopIdxs);
                log && logger.debug('endStopIdxs', endStopIdxs);

                if (shortestRoute) {
                    for (let i = startStopIdxs.length - 1; i >= 0; i--) {
                        for (let j = 0; j < endStopIdxs.length; j++) {
                            if (startStopIdxs[i] < endStopIdxs[j]) {
                                startStopIdx = startStopIdxs[i];
                                endStopIdx = endStopIdxs[j];
                                break;
                            }
                        }
                        if (startStopIdx > -1 && endStopIdx > -1) {
                            break;
                        }
                    }
                } else {
                    for (let j = 0; j < startStopIdxs.length; j++) {
                        for (let i = endStopIdxs.length - 1; i >= 0; i--) {
                            if (startStopIdxs[j] < endStopIdxs[i]) {
                                startStopIdx = startStopIdxs[j];
                                endStopIdx = endStopIdxs[i];
                                break;
                            }
                        }
                        if (startStopIdx > -1 && endStopIdx > -1) {
                            break;
                        }
                    }
                }

            }
            log && logger.debug('start stop idx', firstStop.stopName, ':', startStopIdx);
            log && logger.debug('end stop idx', lastStop.stopName, ':', endStopIdx);
            if (startStopIdx > -1 && endStopIdx > -1) {
                this.startStopIdx = startStopIdx;
                this.endStopIdx = endStopIdx;
            }
        }

        if (log && this.stopTimes.length > 50) {
            console.log(`Took ${Date.now() - time}ms to start/stopIdx for route ${this.routeNumber}`);
        }

        if (ensureStop) {
            const time = Date.now();
            // logger.debug(this.routeNumber, 'waypoints length before', this.waypoints.length);
            log && logger.debug('wp count before', this.waypoints.length);
            let prevWpIdxForStop = 0;
            stops.forEach((stop) => {
                if (prevWpIdxForStop === this.waypoints.length - 1) {
                    prevWpIdxForStop--;
                }
                const nearestSeg = ensureWpAtStop(stop, this, checkDirection, prevWpIdxForStop);
                if (nearestSeg) {
                    prevWpIdxForStop = nearestSeg.segment.nearestWpIdx === nearestSeg.segment.endIdx ? nearestSeg.segment.endIdx + 1 : nearestSeg.segment.endIdx;
                }
            });
            log && logger.debug('wp count after', this.waypoints.length);

            if (this.stopTimes.length > 50) {
                console.log(`Took ${Date.now() - time}ms to ensureWp for route ${this.routeNumber}`);
            }
        }
        // logger.debug(this.routeNumber, 'waypoints length after', this.waypoints.length);
        let prevWpIdxForStop = 0;
        time = Date.now();
        this.waypoints.forEach(wp => {
            delete wp.stopTimeId;
            delete wp.stopId;
            delete wp.stopIndex;
        });
        stops.forEach((stop, idx) => {
            const stopLog = log && false;//stop.stopName.indexOf('Spears Dr at Bren') === 0 || stop.stopName.indexOf('Dubbo Square') === 0

            // If it's the last wp idx, search the last segment for the last stop
            if (prevWpIdxForStop === this.waypoints.length - 1) {
                prevWpIdxForStop--;
            }
            // if(stop.stopName.indexOf('45 Mit') === -1) return
            log && stopLog && logger.debug('Checking stop: ', stop.stopName, 'after wpIdx', prevWpIdxForStop);
            const nearestSeg = getNearestSegment(stop, this.waypoints, {
                fromWpIdx: prevWpIdxForStop,
                log: stopLog,
                furthestSeg: idx === (stops.length - 1)
            });
            if (!nearestSeg) {
                return;
            }

            stop.wpIdx = nearestSeg.segment.nearestWpIdx;
            // @deprecated - use stopId or stopIndex instead
            this.waypoints[nearestSeg.segment.nearestWpIdx].stopTimeId = stop.stopTimeId;
            this.waypoints[nearestSeg.segment.nearestWpIdx].stopTimeIdx = idx;
            stop.nearestSegment = nearestSeg.segment;
            log && stopLog && logger.debug('Stop WP IDX: ', stop.stopName, stop.stopTimeId, stop.wpIdx, nearestSeg.segment);
            prevWpIdxForStop = nearestSeg.segment.nearestWpIdx === nearestSeg.segment.endIdx ? nearestSeg.segment.endIdx + 1 : nearestSeg.segment.endIdx;
        });
        if (this.stopTimes.length > 50) {
            console.log(`Took ${Date.now() - time}ms to nearest segments in stops for route ${this.routeNumber}`);
        }
        let firstPublicStop = stops[this.startStopIdx];
        let lastPublicStop = stops[this.endStopIdx];
        if (firstPublicStop && lastPublicStop) {
            // const nearestStartSeg = ensureWpAtStop(firstPublicStop, this, checkDirection);
            log && logger.debug(firstPublicStop);
            // this.startWpIdx = nearestStartSeg.segment.endIdx;
            this.startWpIdx = firstPublicStop.wpIdx;

            // const nearestLastSeg = ensureWpAtStop(lastPublicStop, this, checkDirection, this.startWpIdx);
            log && logger.debug(lastPublicStop);
            // this.endWpIdx = nearestLastSeg.segment.endIdx;
            this.endWpIdx = lastPublicStop.wpIdx;
        }

        this.services.forEach(trip => {
            trip.startStopIdx = this.startStopIdx;
            trip.endStopIdx = this.endStopIdx;
            trip.stops.forEach((stop, i) => stop.wpIdx = stops[i]?.wpIdx);
            trip.stopTimes.forEach((stop, i) => stop.wpIdx = trip.stops[i]?.wpIdx);
        });

        if (trim) {
            this.trim(firstPublicStop, false);
            this.trim(lastPublicStop, true);
        }

        log && logger.debug('startIdx:', this.startWpIdx, 'ednIdx:', this.endWpIdx, 'startStopIdx:', this.startStopIdx, 'endStopIdx:', this.endStopIdx, ' waypoint count: ', this.waypoints.length);

    }

    getStartEnd(firstStop, lastStop, checkDirection, fromWp) {
        if (firstStop && lastStop) {
            const nearestStartSeg = ensureWpAtStop(firstStop, this, checkDirection, fromWp);
            let startWpIdx = nearestStartSeg.segment.endIdx;
            let nearestLastSeg = ensureWpAtStop(lastStop, this, checkDirection, fromWp);
            let endWpIdx = nearestLastSeg.segment.endIdx;
            while (startWpIdx > endWpIdx) {
                logger.debug('Start wp after end! Trying again from idx: ', startWpIdx, endWpIdx);
                nearestLastSeg = ensureWpAtStop(lastStop, this, checkDirection, startWpIdx);
                endWpIdx = nearestLastSeg.segment.endIdx;
            }
            // Loop
            if (startWpIdx === endWpIdx) {
                startWpIdx = 0;
                endWpIdx = this.waypoints.length - 1;
            }
            return {startWpIdx, endWpIdx};
        }

        return {startWpIdx: 0, endWpIdx: this.waypoints.length};

    }

    getPublicStop(potentialStop, fromIndex = 0, reverse = false) {
        if (potentialStop.stopId) {
            return this.getPublicStops()[this.getPublicStopIndex(potentialStop, fromIndex, reverse)];
        }
    }

    getActiveWaypoints() {
        let startIdx = this.startWpIdx >= 0 ? this.startWpIdx : 0;
        let endIdx = this.endWpIdx ? this.endWpIdx + 1 : this.waypoints.length;
        return this.waypoints.slice(startIdx, endIdx);
    }

    getStartStopSequence() {
        return this.startStopIdx + 1;
    }

    getEndStopSequence() {
        return this.endStopIdx + 1;
    }

    getStartStop(stops = null) {
        stops = stops || this.getPublicStops();
        return stops[this.startStopIdx > -1 ? this.startStopIdx : 0];
    }

    getEndStop(stops = null) {
        stops = stops || this.getPublicStops();
        return stops[this.endStopIdx > -1 && this.endStopIdx < stops.length ? this.endStopIdx : stops.length - 1];
    }

    getStopsBetween(trip) {
        const stopsById = keyBy(this.stops, 'stopId');
        return trip.stopTimes.filter(st => !!stopsById[st.stopId]).map(st => ({...stopsById[st.stopId], ...st}))
            .filter(publicStopFilter)
            .slice(this.startStopIdx + 1, this.endStopIdx)
            .map((stop, idx) => {
                stop.sequence = this.getStartStopSequence() + 1 + idx;
                return stop;
            });
    }


    getStartDepartureTime() {
        return getDepartureTime(this, this.getStartStop());
    }

    getEndArrivalTime() {
        return getDepartureTime(this, this.getEndStop());
    }

    getStartDepartureTimeAsSecondsSinceMidnight() {
        return getDepartureTimeAsSecondsSinceMidnight(this, this.getStartStop());
    }

    getSecondsTilNextDeparture() {
        const secondsSinceMidnight = dayjs().startOf('day').diff(dayjs(), 's');
        const departureSecondsSinceMidnight = this.getStartDepartureTimeAsSecondsSinceMidnight();
        if (secondsSinceMidnight < departureSecondsSinceMidnight) {
            return departureSecondsSinceMidnight - secondsSinceMidnight;
        }
        return departureSecondsSinceMidnight + SECONDS_IN_DAY - secondsSinceMidnight;
    }

    getNextTripBeforeDate(date = dayjs(), school, schedules) {
        return find(this.services.reverse(), trip => {
            if (trip.scheduleIds.some(sId => schedules[sId].isActive(date, !school))) {
                const arrivalTime = getDepartureTimeAsDayjs(trip, trip.getStopTime(this.getEndStop()), null, date);
                return arrivalTime.isSameOrBefore(date, 'd');
            }
            return false;
        });
    }

    getNextTripAfterDate(date = dayjs(), school, schedules) {
        return find(this.services, trip => {
            if (trip.scheduleIds.some(sId => schedules[sId].isActive(date, !school))) {
                const departureTime = getDepartureTimeAsDayjs(trip, trip.getStopTime(this.getStartStop()), null, date);
                return departureTime.isSameOrAfter(date, 'd');
            }
            return false;
        });
    }

    getStopCount() {
        return this.endStopIdx - this.startStopIdx;
    }

    getDuration(trip = this) {
        return getDepartureTimeAsDayjs(trip, this.getEndStop()).diff(getArrivalTimeAsDayjs(trip, this.getStartStop()), 's');
    }

    getDistanceBetween(startIdx, endIdx) {
        startIdx = startIdx > -1 ? startIdx : 0;
        endIdx = endIdx > startIdx && endIdx < this.waypoints.length ? endIdx : this.waypoints.length - 1;
        return this.getDistanceBetweenWpIdxs(startIdx, endIdx);
    }

    getDistance() {
        if (!this.waypoints?.length) {
            return 0;
        }
        
        let startIdx = this.startWpIdx > -1 ? this.startWpIdx : 0;
        let endIdx = this.endWpIdx > 0 ? this.endWpIdx : this.waypoints.length - 1;
        
        // Handle invalid indices
        if (startIdx >= this.waypoints.length) {
            startIdx = this.waypoints.length - 1;
        }
        if (endIdx >= this.waypoints.length) {
            endIdx = this.waypoints.length - 1;
        }
        
        // If start is after end, swap them or return 0 if it doesn't make sense
        if (startIdx > endIdx) {
            console.warn(`Route ${this.routeNumber}: startWpIdx (${startIdx}) > endWpIdx (${endIdx}), swapping indices`);
            [startIdx, endIdx] = [endIdx, startIdx];
        }
        
        return this.getDistanceBetweenWpIdxs(startIdx, endIdx);
    }

    getTravelDistance() {
        return toKmMs(this.getDistance());
    }

    getTravelTime() {
        return toHrsMinsSecs(this.getDuration(), false, true);
    }

    getPublicStops() {
        return this.stops.filter(publicStopFilter);
    }

    getPublicStopTimes() {
        return this.stopTimes.filter(publicStopFilter);
    }

    isLoopRoute() {
        const publicStops = this.getPublicStops();
        return publicStops[0].stopId === last(publicStops).stopId;
    }

    getPublicStopIndex(stop, fromIndex, reverse = false) {
        if (reverse) {
            if (fromIndex === 0) {
                fromIndex = this.getPublicStops().length - 1;
            }
            return findLastIndex(this.getPublicStops(), routeStop => routeStop.stopId === stop.stopId || (routeStop.linkedStops || []).some(ls => ls.stopId === stop.stopId), fromIndex);
        }
        return findIndex(this.getPublicStops(), routeStop => {
            return routeStop.stopId === stop.stopId || (routeStop.linkedStops || []).some(ls => ls.stopId === stop.stopId);
        }, fromIndex);
    }

    getVisiblePublicStops() {
        if (this.startStopIdx > -1 && this.endStopIdx > this.startStopIdx) {
            return this.getPublicStops().slice(this.startStopIdx, this.endStopIdx + 1);
        }
        return [];
    }

    isStopVisible({stop, stopIdx}) {
        stopIdx = Number.isFinite(stopIdx) ? stopIdx : stop ? this.getPublicStopIndex(stop) : -1;
        return this.startStopIdx <= stopIdx && stopIdx <= this.endStopIdx;
    }

    // getStartTimes() {
    //     if (this.hasServices()) {
    //         const tripList = {}
    //         Object.keys(this.trips).forEach(sId => tripList[sId] = this.trips[sId].map(trip => trip.getStartTime()));
    //         return tripList
    //     }
    //     return getDepartureTimeAsDayjs(this, this.getPublicStops()[0]).format('HH:mm');
    // }

    // toGeoJsonLayers({lineWidth = 2, focus = false, unfocus = false, pickable = true}) {
    //     lineWidth = focus ? lineWidth * 2 + 1 : lineWidth;
    //     let path = toPublicGeoJson(this)
    //     const hexColour = hexToRgb(this.colour)
    //     if (unfocus) {
    //         hexColour.push(150);
    //     }
    //     return [new GeoJsonLayer({
    //         id: `route-layer-bg-${this.routeId}`,
    //         data: path,
    //         lineWidthMinPixels: lineWidth * 2,
    //         getLineColor: () => {
    //             return [255, 255, 255]
    //         },
    //         focus: focus,
    //         bg: true
    //     }), new GeoJsonLayer({
    //         id: `route-layer-${this.routeId}`,
    //         data: path,
    //         lineWidthMinPixels: lineWidth,
    //         getLineColor: () => {
    //             return hexColour
    //         },
    //         pickable: pickable,
    //         focus: focus
    //     })];
    // }

    trim(selectedStop, truncate, checkDirection) {
        console.log('Trimming!!', util.inspect(this.services[0].stopTimes[3]));
        logger.debug('WP deltas before trim', this.waypoints.map(wp => wp.delta));
        if (!selectedStop || !this.stops || !this.stops.length || !this.waypoints || this.waypoints.length < 2) {
            logger.debug('Can\'t trim!');
            return;
        }
        // Find closest waypoint
        const segment = ensureWpAtStop(selectedStop, this, checkDirection, -1, truncate)?.segment;
        if (!segment) {
            logger.debug('Can\'t trim as there is no route close enough.');
            return;
        }
        //nearestWp, origin, distance, nearestIdx
        let nearestStopTimeIdx = -1;
        if (selectedStop.stopTimeId) {
            nearestStopTimeIdx = this.stops.findIndex(st => st.stopTimeId === selectedStop.stopTimeId);
        }
        // logger.debug(this.waypoints.length)
        if (truncate) {
            if (nearestStopTimeIdx > -1) {
                this.stops.splice(nearestStopTimeIdx + 1);
                this.stopTimes.splice(nearestStopTimeIdx + 1);
                this.services.forEach(trip => {
                    trip.stopTimes.splice(nearestStopTimeIdx + 1);
                    trip.stops.splice(nearestStopTimeIdx + 1);
                });
            }
            logger.debug('Trimming from ', segment.nearestWpIdx + 1, ' to ', this.waypoints.length);
            this.waypoints.splice(segment.nearestWpIdx + 1, this.waypoints.length);
        } else {
            if (nearestStopTimeIdx > -1) {
                this.stops.splice(0, nearestStopTimeIdx);
                this.stopTimes.splice(0, nearestStopTimeIdx);
                this.services.forEach(trip => {
                    trip.stopTimes.splice(0, nearestStopTimeIdx);
                    trip.stops.splice(0, nearestStopTimeIdx);
                });
            }
            logger.debug('Trimming from 0 to ', segment);
            this.waypoints.splice(0, segment.nearestWpIdx);
            updateRouteDeltas(0, this);
        }
        // logger.debug(this.waypoints.length)
        // this.stops = this.stops.filter(stop => getNearestSegment(stop, this.waypoints).distance < 50);
        this.stops.forEach(stop => delete stop.wpIdx);
        logger.debug('Finish trim');
        logger.debug('WP deltas after trim', this.waypoints.map(wp => wp.delta));

        return this;
    }

    cut(startWpIdx, endWpIdx, removeRoutes) {
        if (endWpIdx <= startWpIdx) {
            logger.debug('Invalid indexes. End must be after start');
            return;
        }
        logger.debug('Cutting!!');
        logger.debug('WP deltas before trim', this.waypoints.map(wp => wp.delta));
        if (this.waypoints?.length <= startWpIdx) {
            logger.debug('Can\'t cut! Length: %d, StartIdx: %d, EndIdx: %d', this.waypoints?.length, startWpIdx, endWpIdx);
            return;
        }

        endWpIdx = endWpIdx > this.waypoints?.length - 1 ? this.waypoints.length - 1 : endWpIdx;

        // Find any stops between idx's
        this.calculateStartEnd({stops: this.stops});
        if (removeRoutes) {
            const stopIdxsToRemove = this.stops.reduce((a, e, i) => {
                if (e.wpIdx >= startWpIdx && e.wpIdx <= endWpIdx) {
                    a.push(i);
                }
                return a;
            }, []);
            this.stops.splice(stopIdxsToRemove[0], stopIdxsToRemove.length);
            this.stopTimes.splice(stopIdxsToRemove[0], stopIdxsToRemove.length);
            this.services.forEach(trip => {
                trip.stopTimes.splice(stopIdxsToRemove[0], stopIdxsToRemove.length);
                trip.stops.splice(stopIdxsToRemove[0], stopIdxsToRemove.length);
            });
        }
        logger.debug('Cutting Wps from ', startWpIdx, ' to ', endWpIdx);
        this.waypoints.splice(startWpIdx, endWpIdx - startWpIdx + 1);
        if (!this.waypoints.length) {
            // if all waypoints deleted then we shouldn't have any stops, so remove any stray ones.
            this.stops.length = 0;
            this.stopTimes.length = 0;
            this.services.forEach(trip => {
                trip.stopTimes.length = 0;
                trip.stops.length = 0;
            });
        }

        updateRouteDeltas(0, this);

        this.calculateStartEnd({stops: this.stops});

        logger.debug('Finish cut');
        logger.debug('WP deltas after cut', this.waypoints.map(wp => wp.delta));
        return this;
    }

    updateStopPoints() {
        if (this.stops && this.stops.length && this.waypoints && this.waypoints.length) {
            logger.debug(`Looking for missing stop points...`);
            this.stops.forEach((stop, idx) => {
                const {segment, distance} = getNearestSegment(stop, this.waypoints, false, -1, 0, 0);
                logger.debug(`${distance} < ${segment.distanceToNearestWp}`);
                if (distance < segment.distanceToNearestWp) {
                    logger.debug(`Adding new waypoints at stop ${idx + 1} @ ${segment.startIdx}: ${util.inspect(segment.calculatedClosestWp)}`);
                    this.waypoints.splice(segment.startIdx + 1, 0, segment.calculatedClosestWp);
                }
            });
        }
    }

    getStopBeforeWpIndex(wpIdx, stops = this.getPublicStops()) {
        logger.debug('looking for stop before', wpIdx);
        // assumes calculateStartEnd has been run on all stops
        const publicStops = stops;
        if (!publicStops.length || wpIdx < publicStops[0].wpIdx) {
            return;
        }
        for (let i = 1; i < publicStops.length; i++) {
            const stop = publicStops[i];
            if (stop.wpIdx > wpIdx) {
                return publicStops[i - 1];
            }
        }
        return last(publicStops);
    }

    getStopAfterWpIndex(wpIdx, stops = this.getPublicStops()) {
        // assumes calculateStartEnd has been run on all stops
        const publicStops = stops;
        for (let i = 0; i < publicStops.length; i++) {
            const stop = publicStops[i];
            if (stop.wpIdx > wpIdx) {
                return stop;
            }
        }
    }

    getDistanceBetweenWpIdxs(idx1, idx2) {
        // Additional safety checks
        if (!this.waypoints || this.waypoints.length === 0) {
            return 0;
        }
        
        // Ensure indices are within bounds
        idx1 = Math.max(0, Math.min(idx1, this.waypoints.length - 1));
        idx2 = Math.max(0, Math.min(idx2, this.waypoints.length - 1));
        
        if (idx1 < 0 || idx1 > idx2) {
            throw new Error(`invalid indexes, idx1: ${idx1}, idx2: ${idx2}, this.waypoints.length: ${this.waypoints.length}`);
        }
        
        let prevWp, dist = 0;
        for (let i = idx1 + 1; i <= idx2; i++) {
            prevWp = this.waypoints[i - 1];
            const wp = this.waypoints[i];
            if (prevWp && wp) {
                dist += getDistanceInMetres(prevWp, wp);
            }
        }
        return dist;
    }

    getAvgSpdBetweenStops(stop1, stop2) {
        logger.debug('Getting distance between %s and %s. WpIdxs: %d, %d, Deltas: %d, %d', stop1.stopName, stop2.stopName, stop1.wpIdx, stop2.wpIdx, stop1.departSecs, stop2.arriveSecs);
        const dist = this.getDistanceBetweenWpIdxs(stop1.wpIdx, stop2.wpIdx);
        logger.debug('Distance between %s to %s: %d', stop1.stopName, stop2.stopName, dist);
        const time = stop2.arriveSecs - stop1.departSecs;
        return time > 0 ? dist / time : 0;
    }

    getTripCount() {
        return this.tripCount;
    }

    hasLogo() {
        return hasLogo(this);
    }

    stopsAtStopInWindow(stop, timeFilter, end) {
        if (!find(this.stops, {stopId: stop.stopId})) {
            return false;
        }
        if (end === (timeFilter.type === TimeFilterType.ARRIVING)) {
            return this.services.filter(trip => trip.schedules.filter(schedule => schedule.isActive(timeFilter.startTime, !timeFilter.anyDate)).length)
                .some(trip => {
                    return trip.stopTimes.filter(st => stop.stopId === st.stopId).some(st => {
                        const departureTime = getDepartureTimeAsSecondsSinceMidnight(trip, st);
                        if (timeFilter.anyDate) {
                            if (timeFilter.type === TimeFilterType.ARRIVING) {
                                if (departureTime < (stop.startBell - stop.startBellWindow) || departureTime > stop.startBell) {
                                    return false;
                                }
                            } else {
                                if (departureTime < stop.endBell || departureTime > (stop.endBell + stop.endBellWindow)) {
                                    return false;
                                }
                            }
                        }
                        return departureTime >= timeFilter.getStartTime() && departureTime <= timeFilter.getEndTime();
                    });
                });
        }
        return true;
    }

    stopToStopTimesInWindow(stop, tripId, timeFilter, end) {
        if (!find(this.stops, {stopId: stop.stopId})) {
            return [];
        }

        const _this = this;
        const trip = find(this.services, {'tripId': tripId});
        return trip.stopTimes.filter(publicStopFilter).filter(st => {
            if (stop.stopId !== st.stopId) {
                return false;
            }
            // This will check the stoptime is within the time filter
            if (timeFilter) {
                const windowStart = timeFilter.getStartTimeAsDayJs();
                const windowEnd = timeFilter.getEndTimeAsDayJs();
                const timeToCheck = timeFilter.checkArrivalTime(_this.direction) ? getArrivalTimeAsSecondsSinceMidnight(trip, st) : getDepartureTimeAsSecondsSinceMidnight(trip, st);
                if (stop.startBell && stop.startBellWindow && timeFilter.anyDate && timeFilter.checkArrivalTime(_this.direction)) {
                    if (timeToCheck < (stop.startBell - stop.startBellWindow) || timeToCheck > stop.startBell) {
                        return false;
                    }
                } else if (stop.endBell && stop.endBellWindow && timeFilter.anyDate && timeFilter.checkDepartureTime(_this.direction)) {
                    if (timeToCheck < stop.endBell || timeToCheck > (stop.endBell + stop.endBellWindow)) {
                        return false;
                    }
                }

                if (timeFilter.anyDate) {
                    return timeToCheck >= timeFilter.getStartTime() && timeToCheck <= timeFilter.getEndTime();
                }
                // const startTime = dayjs().startOf('d').add(timeFilter.getStartTime(), 's')
                // const endTime = dayjs().startOf('d').add(timeFilter.getEndTime(), 's')
                let time = timeFilter.startTime.clone().startOf('d').add(timeToCheck, 's');
                if (time.isBefore(timeFilter.startTime)) {
                    time = time.add(1, 'd');
                }

                return time.isBetween(windowStart, windowEnd, 's', '[]');
            }
            return true;
        }).map(st => new BaseStop({...stop, ...st}));
    }

    getWpPassingTimesForTrip(trip) {
        let currentTime = trip.getStartTimeAsSecondsSinceMidnight();
        let wpPassingTimes = [];
        trip.stopTimes.forEach((st, i, sts) => {
            if (i === 0) {
                return;
            }
            const prevSt = sts[i - 1];
            const stop = this.stops ? this.stops[i] : trip.stops ? trip.stops[i] : null;
            const prevStop = this.stops ? this.stops[i - 1] : trip.stops ? trip.stops[i - 1] : null;

            const start = prevSt.departSecs;
            const end = st.arriveSecs;
            const time = end - start;
            const dist = (stop?.distance || 0) - (prevStop?.distance || 0);
            const avgSpd = dist / time;
            wpPassingTimes = wpPassingTimes.concat(this.waypoints.slice(prevStop?.wpIdx || 0, stop?.wpIdx || this.waypoints.length - 1).map((wp, i, wps) => {
                if (i === 0) {
                    return currentTime;
                }
                const prevWp = wps[i - 1];
                const dist = getDistanceInMetres(wp, prevWp);
                currentTime += dist / avgSpd;
                return currentTime;
            }));
        });
        return wpPassingTimes;
    }

    getRestrictedPointsOnRoute(restrictedPoints) {
        if (!this.waypoints?.length || !restrictedPoints?.length) {
            return [];
        }

        const touchingPoints = new Set();
        for (let i = 0; i < this.stops.length - 1; i++) {
            const startWp = this.stops[i].wpIdx;
            const endWp = this.stops[i + 1].wpIdx;

            for (let j = startWp; j < endWp - 1; j++) {
                const wp1 = this.waypoints[j];
                const wp2 = this.waypoints[j + 1];

                for (const restrictedPoint of restrictedPoints) {
                    const nearestSegment = getNearestSegment(restrictedPoint, [wp1, wp2]);
                    if (nearestSegment?.segment && nearestSegment.segment.dist < 20) {
                        touchingPoints.add(restrictedPoint);
                    }
                }
            }
        }
        return Array.from(touchingPoints);
    }

    isPassingThroughRestrictedPoint(restrictedPoints) {
        return this.getRestrictedPointsOnRoute(restrictedPoints)?.length;
    }

    validate(restrictedPoints, allStops = null, allTransfers = null, driverDutiesByTripId = null) {
        this.calculateStartEnd({ensureStop: false});
        this.calculateDistances();
        // Filter out placeholder stops
        const realStops = this.stops?.filter(s => !s.placeholder);
        const messages = {global: [], stops: [], isValid: true};
        if (!this.routeNumber?.length) {
            messages.global.push({msg: '⛔️ Route number cannot be empty (REF-019)'});
            messages.routeNumber = true;
        }
        if (!this.routeName?.length) {
            messages.global.push({type: 'routeName', msg: '⛔️ Route name cannot be empty (REF-018)'});
            messages.routeName = true;
        }
        if (restrictedPoints) {
            if (this.isPassingThroughRestrictedPoint(restrictedPoints)) {
                messages.global.push({msg: '⛔️ Route is passing through a restricted point (REF-086)'});
            }
        }
        messages.stops = realStops.map(() => []);
        if (realStops) {
            realStops.forEach((stop, idx) => {
                if (idx > 0) {
                    if (!stop.wpIdx || stop.wpIdx <= realStops[idx - 1].wpIdx) {
                        messages.stops[idx]?.push({msg: `⛔️ Stop point is out of sequence (REF-023) ${stop.wpIdx} <= ${realStops[idx - 1].wpIdx}`});
                    }
                }
                // HACK: This is a hack to prevent this validation when there is only one waypoint. There cannot be a nearest segment in this case.
                if (this.waypoints?.length > 1 && (!stop.nearestSegment || stop.nearestSegment.dist > 20)) {
                    // messages.stops[idx]?.push({msg: `Stop '${stop.stopName}' at sequence ${idx + 1} can't be mapped to the route.`});
                    messages.stops[idx]?.push({msg: `⛔️ Stop point is more than 20m from ${idx === realStops.length - 1 ? 'the end of the' : idx === 0 ? 'the start of the' : ''} route path (REF-001)`});
                }
                if (!this.charter && stop.stopType && stop.stopType !== 'bus') {
                    messages.stops[idx]?.push({msg: `⛔️ Point type ${stop.stopType} is not allowed. Use bus stop point type only (REF-002)`});
                }
            });
        }
        // if (this?.services) {
        //     let anyMissingDuty = false;
        //     let anyOverlappingDuty = false;
        //     this.services.forEach(trip => {
        //         // --- DRIVER DUTY VALIDATION ---
        //         if (driverDutiesByTripId) {
        //             const duties = driverDutiesByTripId[trip.tripId] || [];
        //             if (!duties.length) {
        //                 anyMissingDuty = true;
        //             }
        //             for (const duty of duties) {
        //                 const { overlappingRowIds } = detectDutyOverlaps(duty.rows || []);
        //                 if (overlappingRowIds.size > 0) {
        //                     anyOverlappingDuty = true;
        //                 }
        //             }
        //         }
        //     });
        //     // After all trips, push summary messages if needed
        //     if (anyMissingDuty) {
        //         messages.global.push({ msg: '⛔️ No driver duty assigned to some service trips.' });
        //     }
        //     if (anyOverlappingDuty) {
        //         messages.global.push({ msg: '⛔️ Some service trips have overlapping driver duty segments.' });
        //     }
        // }
        if (this?.services) {
            this.services.forEach(trip => {

                if (trip.stops?.length < 2) {
                    messages.global.push({msg: '⛔️ Published routes must have at least two bus stop points (REF-012)'});
                    return;
                }

                let prevDepartureTime = 0, nextArrivalTime = -1;
                for (const [idx, stopTime] of trip.stops.entries()) {
                    if (idx === trip.stops.length - 1) {
                        nextArrivalTime = -1;
                    } else {
                        nextArrivalTime = trip.getArrivalTimeAsDayjs(trip.stops[idx + 1]).unix();
                    }
                    const currentArriveTime = trip.getArrivalTimeAsDayjs(stopTime).unix();
                    const currentDepartTime = trip.getDepartureTimeAsDayjs(stopTime).unix();
                    if (prevDepartureTime > 0 && currentArriveTime < prevDepartureTime) {
                        messages.stops[idx]?.push({
                            tripId: trip.tripId,
                            arrive: true,
                            msg: (`⛔️ Arrival time is before the departure time of the previous stop (REF-003)`)// on the ${trip.getStartTime()} service${trip.stops ? `at stop ${idx + 1} ${trip.stops[idx]?.stopName}` : ''}`)
                        });
                    }
                    if (nextArrivalTime > -1 && currentDepartTime > nextArrivalTime) {
                        messages.stops[idx]?.push({
                            tripId: trip.tripId,
                            depart: true,
                            msg: (`⛔️ Departure time is after the arrival time of the next stop (REF-004)`)// on the ${trip.getStartTime()} service${trip.stops ? ` at stop ${idx + 1} ${trip.stops[idx]?.stopName}` : ''}`)
                        });
                    }
                    if (currentDepartTime < currentArriveTime) {
                        messages.stops[idx]?.push({
                            tripId: trip.tripId,
                            depart: true,
                            msg: (`⛔️ Departure time is before the arrival time (REF-005)`)// on the ${trip.getStartTime()} service${trip.stops ? ` at stop ${idx + 1} ${trip.stops[idx]?.stopName}` : ''}`)
                        });
                    }
                    if (realStops?.length && idx > 0) {
                        const time = currentArriveTime - prevDepartureTime;
                        const dist = (realStops[idx]?.distance || 0) - (realStops[idx - 1]?.distance || 0);
                        const speed = dist / (time || 1);
                        if (speed > METRES_PER_SEC) {
                            messages.stops[idx]?.push({
                                tripId: trip.tripId,
                                arrive: true,
                                warning: true,
                                // msg: (`Speed violation greater than 120km/hr departing from previous stop point. Distance between stops: ${toKmMs(dist, 1, 2000)}. Minimum travel time: ${toHrsMinsSecs(dist/METRES_PER_SEC)}.`)
                                msg: (`⚠️ Speed exceeds 120km/hr departing from previous stop. Minimum travel time of ${toHrsMinsSecs(dist / METRES_PER_SEC)} required to travel ${toKmMs(dist, 1)} (REF-006)`)
                            });
                        }
                    }
                    if (idx > 0 && currentArriveTime === prevDepartureTime) {
                        messages.stops[idx]?.push({
                            tripId: trip.tripId,
                            arrive: true,
                            msg: (`⛔️ Arrival time is the same as previous stop's departure time`)
                        });
                    }
                    
                    // Transfer validation
                    if (stopTime.transfersTo?.length > 0) {
                        stopTime.transfersTo.forEach(transfer => {
                            if (transfer.invalid || transfer.warnings?.length > 0) {
                                messages.stops[idx]?.push({
                                    tripId: trip.tripId,
                                    msg: `⛔️ Transfer connection has validation errors (REF-024)`
                                });
                            }
                        });
                    }
                    
                    if (stopTime.transfersFrom?.length > 0) {
                        stopTime.transfersFrom.forEach(transfer => {
                            if (transfer.invalid || transfer.warnings?.length > 0) {
                                messages.stops[idx]?.push({
                                    tripId: trip.tripId,
                                    msg: `⛔️ Pickup connection has validation errors (REF-025)`
                                });
                            }
                        });
                    }
                    
                    // School bell validation (using shared utilities from model/school.js)
                    if (allStops && trip.getRouteType() === 'School') {
                        const currentStop = allStops[stopTime.stopId];
                        if (currentStop) {
                            // Find linked school stops using shared utility
                            const linkedSchoolStops = findLinkedSchoolStops(currentStop.stopId, allStops);

                            linkedSchoolStops.forEach(schoolStop => {
                                // Use shared validation utility
                                const validation = validateBellWindow(
                                    stopTime.arriveSecs,
                                    stopTime.departSecs,
                                    schoolStop
                                );

                                // Invalid if bell times are configured but neither window is satisfied
                                if (validation.hasBellTimes && !validation.isValid) {
                                    messages.stops[idx]?.push({
                                        tripId: trip.tripId,
                                        msg: `⛔️ Service times do not align with school bell windows at ${schoolStop.stopName} (REF-026)`
                                    });
                                    // check if global message for school bell misalignment already exists
                                    if (!messages.global.some(m => m.msg === '⛔️ Some service trip school bell times are not aligned.')) {
                                        messages.global.push({ msg: '⛔️ Some service trip school bell times are not aligned.' });
                                    }
                                }
                            });
                        }
                    }
                    
                    prevDepartureTime = currentDepartTime;
                }

                if (!this.charter && (!trip.stops[0].timingPoint || !last(trip.stops).timingPoint)) {
                    messages.global.push({
                        msg: '⛔️ The first and last stop of a published route must be a timing point (REF-015)'
                    });
                }
                if (!this.charter && !trip.stops[0].timingPoint) {
                    messages.stops[0]?.push({
                        tripId: trip.tripId,
                        msg: '⛔️ First stop point must be a timing point (REF-016)',
                        stop: true
                    });
                }

                if (!this.charter && !last(trip.stops).timingPoint) {
                    messages.stops[messages.stops.length - 1]?.push({
                        tripId: trip.tripId,
                        msg: '⛔️ Last stop point must be a timing point (REF-017)',
                        stop: true
                    });
                }

                if (!this.charter && !trip.scheduleIds?.length) {
                    messages.global.push({
                        tripId: trip.tripId,
                        type: 'schedule',
                        msg: '⛔️ This service has missing or expired operating calenders (REF-020)'
                    });
                    messages.schedule = true;
                }
            });
            if (realStops?.length) {
                // eslint-disable-next-line
                realStops.forEach((stop, idx) => {
                    if (!this.charter && !stop.verified) {
                        messages.stops[idx]?.push({msg: `⛔️ Point '${stop.stopName}' has not been verified (REF-007)`});
                    }
                    if (idx > 0 && realStops[idx - 1].stopId === stop.stopId) {
                        messages.stops[idx]?.push({msg: `⛔️ Point '${stop.stopName}' is a duplicate (REF-008)`});
                    }
                });

                if (!this.charter && isFinite(realStops[0].wpIdx) && realStops[0].wpIdx !== 0) {
                    messages.stops[0]?.push({
                        warning: true,
                        msg: '⛔️ Route must start with no leading waypoints (REF-009)'
                    });
                }
                if (!this.charter && isFinite(last(realStops).wpIdx) && last(realStops).wpIdx !== this.waypoints.length - 1) {
                    messages.stops[realStops.length - 1]?.push({
                        warning: true,
                        msg: '⛔️ Route must end with no trailing waypoints (REF-010)'
                    });
                }
            }
            if (!this.services?.length) {
                messages.global.push({msg: '⛔️ Published routes must have at least one service timetable (REF-011)'});
            }
            if (realStops?.length && realStops.filter(publicStopFilter).length < 2) {
                messages.global.push({msg: '⛔️ Published routes must have at least two bus stop points (REF-012)'});
            }
            if (!this.charter && realStops?.length && realStops.filter(publicStopFilter).length !== realStops.length) {
                messages.global.push({msg: '⛔️ Published routes must only contain bus stop types (REF-013)'});
            }

            if (this.stops?.length < 2) {
                messages.global.push({msg: '⛔️ Published routes must have at least 2 stop times (REF-014)'});
            }
        }

        messages.isValid = !messages.global?.length && !messages.stops.some(s => s?.length) && !messages.schedule && !messages.routeNumber && !messages.routeName;
        messages.isWarning = messages.global.some(m => m.warning) || messages.stops.some(s => s.some(m => m.warning));
        messages.isTripInvalid = tripId => {
            return messages.global?.length ||
                messages.stops.some(s => s.some(st => !st.tripId || st.tripId === tripId));
        };
        return messages;
    }

    validateAsList(restrictedPoints, allStops = null, driverDutiesByTripId = null) {
        let messages = this.validate(restrictedPoints, allStops, null, driverDutiesByTripId);
        if (messages.isValid && !messages.isWarning) {
            return [];
        }
        const result = messages.global || [];
        const stopValidationMessges = flatten(messages.stops).filter(m => !!m).length;
        const error = flatten(messages.stops).some(m => !m.warning);
        if (stopValidationMessges && error) {
            result.push({
                msg: (error ? '⛔ ' : '⚠️ ') + stopValidationMessges + ' stop time validation ' +
                    (error ? 'errors' : 'warnings') + ' (REF-022)'
            });
        }
        return result.map(m => m.msg);
    }

    breakSubRoutes() {
        let subRoutes = {};
        this.waypoints.forEach((wp, idx) => {
            const prevWp = this.waypoints[idx - 1];
            let subRoute = {range: {first: idx, last: idx}, waypoints: []};
            if (wp?.stopTimeId || wp?.checkpoint || prevWp?.stopTimeId || prevWp?.checkpoint) {
                subRoute.waypoints.push({...wp, x: wp.lon, y: wp.lat});
                subRoutes[idx] = subRoute;
            } else if (!wp.stopTimeId && !wp.checkpoint) {
                subRoute = subRoutes[idx - 1] || subRoute;
                subRoute.waypoints.push({...wp, x: wp.lon, y: wp.lat});
                subRoute.range.last = idx;
                subRoutes[idx] = subRoute;
                delete subRoutes[idx - 1];
            }
        });
        return subRoutes;
    }

    optimiseWaypointResult() {
        const _route = this.clone();
        const subRoutes = _route.breakSubRoutes();
        const total = values(subRoutes).filter(route => route.waypoints?.length > 1).reduce((total, subRoute) => {
            total += simplify(subRoute.waypoints, WP_OPTIMISE_RATE).length;
            return total;
        }, 0);
        return this.waypoints.length - total - _route.waypoints.filter(wp => wp.stopTimeId || wp.checkpoint).length;
    }

    optimiseWaypoints() {
        const _route = this.clone();
        console.log(`WPs before optimisation: ${this.waypoints.length}`);
        let subRoutes = _route.breakSubRoutes();
        values(subRoutes).filter(subRoute => subRoute.waypoints.length > 1).forEach(subRoute => {
            subRoute.waypoints = simplify(subRoute.waypoints, WP_OPTIMISE_RATE);
        });
        this.waypoints = flatten(values(subRoutes).map(subRoute => subRoute.waypoints));
        this.waypoints.forEach(wp => {
            delete wp.x;
            delete wp.y;
        });
        console.log(`WPs after optimisation: ${this.waypoints.length}`);
        this.calculateStartEnd({});
    }

    optimiseCheckpoints() {
        const checkpoints = this.waypoints.filter(wp => wp.checkpoint);
        checkpoints.forEach(wp => {
            wp.id = ulid();
            wp.x = wp.lon;
            wp.y = wp.lat;
        });
        let optimisedCheckpoints = checkpoints.map(cp => ({...cp}));
        console.log(`WPs before optimisation: ${optimisedCheckpoints.length}`);
        optimisedCheckpoints = simplify(optimisedCheckpoints, 0.00001);
        const optimisedCheckpointsById = keyBy(optimisedCheckpoints, 'id');
        console.log(`WPs after optimisation: ${optimisedCheckpoints.length}`);
        this.waypoints.filter(wp => wp.checkpoint).forEach(cp => {
            cp.checkpoint = !!optimisedCheckpointsById[cp.id];
        });

        this.waypoints.forEach(wp => {
            delete wp.x;
            delete wp.y;
            delete wp.id;
        });
    }

    updateTripDates(dates) {
        let datesArr = [];
        if (!isArray(dates)) {
            datesArr = [dates];
        }
        this.services.forEach(trip => {
            trip.dates = datesArr;
        });
    }

    toGeoJson() {
        return toLineString(this);
    }

    toJson() {
        const {
            userId,
            charter,
            startDepot,
            endDepot,
            returnEnabled,
            routeId,
            published,
            scheduleId,
            colour,
            direction,
            routeLogo,
            routeType,
            routeNumber,
            driverShift,
            vehicleShift,
            vehicleTypes,
            vehicleTypeQuantities,
            routeName,
            routeLabel,
            routeDescription,
            routeDetails,
            routeHeadsign,
            waypoints,
            stopTimes,
            startTime,
            services,
            author,
            editor,
            source,
            username,
            createdAt,
            updatedAt,
            lastEditor,
            approvedBy,
            approvedAt,
            publishedBy,
            publishedAt,
            unpublishedBy,
            unpublishedAt,
            contractId,
            warnings,
            distance,
            duration,
            startDepotDistance,
            startDepotDuration,
            endDepotDistance,
            endDepotDuration,
            passengerCount,
            returnDistance,
            returnDuration,
            dates
        } = this;

        let routeToUpdate = {
            userId,
            routeId,
            charter,
            returnEnabled,
            published,
            scheduleId,
            colour,
            direction,
            routeLogo,
            routeType,
            routeNumber,
            driverShift,
            vehicleShift,
            vehicleTypes,
            vehicleTypeQuantities,
            routeName,
            routeLabel,
            routeDescription,
            routeDetails,
            routeHeadsign,
            waypoints,
            stopTimes,
            startTime,
            services,
            author,
            editor,
            source,
            createdAt,
            username,
            updatedAt,
            lastEditor,
            approvedBy,
            approvedAt,
            publishedBy,
            publishedAt,
            unpublishedBy,
            unpublishedAt,
            contractId,
            warnings,
            distance,
            duration,
            passengerCount,
            returnDistance,
            returnDuration,
            startDepot,
            startDepotDistance,
            startDepotDuration,
            endDepot,
            endDepotDistance,
            endDepotDuration,
            dates: dates?.map(d => typeof d === 'string' ? d : normaliseDate(d)?.format(DATE_STRING))
        };
        routeToUpdate.routeName = routeToUpdate.routeName || routeToUpdate.routeNumber;
        routeToUpdate.createdAt = routeToUpdate.createdAt?.valueOf() || Date.now();
        routeToUpdate.updatedAt = routeToUpdate.updatedAt?.valueOf() || Date.now();
        routeToUpdate.stopTimes?.forEach(s => {
            delete s.avgSpd;
            delete s.linkTime;
            delete s.transfersFrom;
            delete s.transfersTo;
            s.date = typeof s.date === 'string' ? s.date : s.date?.format ? s.date?.format(DATE_STRING) : s.date;
            Object.keys(s).forEach(key => {
                if (s[key] === null) {
                    delete s[key];
                }
            });
        });

        // Remove any invalid Trips

        // routeToUpdate.services = routeToUpdate.services.filter(trip => {
        //     return trip.scheduleIds?.length
        // })

        routeToUpdate.services = routeToUpdate.services?.map(trip => {
            trip = {...trip, dates: trip.dates?.map(d => typeof d === 'string' ? d : d?.format(DATE_STRING))};
            trip.stopTimes = trip.stopTimes?.map(st => ({...st, date: typeof st.date === 'string' ? st.date : st.date?.format ? st.date.format(DATE_STRING) : null}));
            delete trip.transfersFrom;
            delete trip.transfersTo;
            delete trip.stops;
            delete trip.route;
            return trip;
        });

        Object.keys(routeToUpdate).forEach(key => {
            if (routeToUpdate[key] === null) {
                delete routeToUpdate[key];
            }
        });
        return routeToUpdate;
    }

}

export class Transfer extends Model {
    constructor(data) {
        super(data);
        this.startRouteId = null;
        this.startStopSequence = 0;
        this.endRouteId = null;
        this.endStopSequence = 0;
        this.walkingTime = null;
        this.walkingDist = null;
        Object.assign(this, data);
    }
}

export class Point extends Model {

    constructor(data) {
        super();
        this.lat = 0.0;
        this.lon = 0.0;
        Object.assign(this, data);
    }

    isValid() {
        return Number.isFinite(this.lat) && Number.isFinite(this.lon) && this.lat >= -90 && this.lat <= 90 && this.lon >= -180 && this.lon <= 180;
    }

    distanceToWayPoint(wp) {
        return getDistanceInMetres(this, wp);
    }

}

export class WayPoint extends Point {
    constructor(data) {
        super(data);
        this.checkpoint = false;
        this.delta = 0; // Time in seconds since the start of the route
        this.distance = 0; // Distance in metres from the start of the route
        Object.assign(this, data);
        this.delta = Math.round(this.delta);
        // this.delta = Math.ceil(this.delta / 60.0) * 60;
    }
}

export const StopTypes = {
    bus: {type: 'bus', name: 'Bus', shortName: 'B', title: 'Bus stop'},
    school: {type: 'school', name: 'School', shortName: 'S', title: 'School'},
    depot: {type: 'depot', name: 'Depot', shortName: 'D', title: 'Depot'},
    charter: {type: 'charter', name: 'Charter', shortName: 'C', title: 'Charter stop'},
    busInterchange: {type: 'bus_interchange', name: 'Bus Interchange', shortName: 'BI', title: 'Bus interchange'},
    schoolInterchange: {
        type: 'school_interchange',
        name: 'School Interchange',
        shortName: 'SI',
        title: 'School interchange'
    },
    restricted: {type: 'restricted', name: 'Restricted', shortName: 'R', title: 'Road restriction'},
    nonpub: {type: 'nonpub', name: 'Non-public', shortName: 'N', title: 'Non-public'}
};

export class Stop extends WayPoint {

    static simplifyStopName(stopName) {
        return stopName.toLowerCase().replace(/[^a-z]/g, '');
    }

    static sort(a, b) {
        if (a.master && b.master) {
            return a.stopName.localeCompare(b.stopName);
        } else if (a.master) {
            return -1;
        } else if (b.master) {
            return 1;
        } else {
            if (a.authorityId && b.authorityId) {
                return a.stopName.localeCompare(b.stopName);
            } else if (a.authorityId) {
                return -1;
            } else if (b.authorityId) {
                return 1;
            } else {
                if (a.stopCode && b.stopCode) {
                    return a.stopName.localeCompare(b.stopName);
                } else if (a.stopCode) {
                    return -1;
                } else if (b.stopCode) {
                    return 1;
                } else {
                    if (a.verified !== b.verified) {
                        return b.verified - a.verified;
                    } else {
                        return b.updatedAt - a.updatedAt;
                    }
                }
            }
        }
    }

    constructor(data) {
        super(data);
        this.stopId = null;
        this.stopName = null;
        this.stopDesc = null;
        this.stopType = null; // StopType
        this.stopCode = null;
        this.linkedStops = null; // [{LinkedStop}] from DB
        this.imported = false; // Imported stop from a CSV file
        this.authorityId = null; // Downloaded stop from the master stop list, or imported stop that had a stopCode (australia#tfnsw)
        this.master = false; // master stop from the cloud
        this.outOfSync = null; // Stop with stopCode that is out of sync with the master stop of the same stop code with the same authorityId
        this.routes = [];
        this.startBell = 0;
        this.startBellWindow = 0;
        this.endBell = 0;
        this.endBellWindow = 0;
        this.transferWindow = 0; // Transfer window in minutes
        Object.assign(this, data);
        if (typeof this.outOfSync === 'string') {
            this.outOfSync = [this.outOfSync];
        }
        this.center = [this.lon, this.lat];
    }

    getAllRoutes() {
        let routes = this.routes;
        if (this.hasLinkedStops()) {
            this.linkedStops.forEach(ls => routes = routes.concat(ls.stop.routes));
        }
        return routes;
    }

    isLinkedStop(stop) {
        return this.linkedStops?.findIndex(ls => ls.stopId === stop?.stopId) > -1;
    }

    hasLinkedStops() {
        return !!this.linkedStops?.length;
    }

    hasTransfers() {
        return !!this.routes?.some(r => r.transfers?.length);
    }

    canTransfer() {
        return this.transferWindow > 0;
    }

    unsetLinkedStops() {
        this.linkedStops?.forEach(ls => delete ls.stop);
    }

    setLinkedStops(stopsById, includeStopModel = true) {
        if (Array.isArray(stopsById)) {
            stopsById = keyBy(stopsById, 'stopId');
        }
        this.linkedStops = this.linkedStops?.filter(linkedStop => stopsById[linkedStop.stopId]).map(linkedStop => {
            const stop = stopsById[linkedStop.stopId];
            if (includeStopModel) {
                linkedStop.stop = new Stop(cloneDeep(stop));
                delete linkedStop.stop.linkedStops;
            }
            // this.routes = (this.routes || []).concat(stop?.routes || [])
            return new LinkedStop(linkedStop);
        }).filter(ls => !!ls);
        this.routes = uniqBy(this.routes, 'routeId');
        this.routes?.forEach(r => {
            r.transfers?.forEach(t => {
                t.fromStop = stopsById[t.fromStopId];
                t.toStop = stopsById[t.toStopId];
            });
        });
        return this.linkedStops;
    }

    getPassingTime(routeId, tripId, stopTimeId) {
        const r = find(this.routes, r => r.routeId === routeId);
        if (!r) return;
        const t = r.services[tripId];
        if (!t) return;
        return find(t.passingTimes, pt => pt.stopTimeId === stopTimeId);
    }

    getPassingTimes(routeFilter, {date = dayjs(), school, time, window: windowSecs = 0}, allSchedules) {

        const today = dayjs().year(date.year()).month(date.month()).date(date.date());
        const windowStart = date.clone().startOf('d').add(time, 's');
        const windowEnd = windowStart.clone().add(windowSecs, 's');

        const passingTimes = [];
        (this.routes || []).filter(routeFilter).forEach(r => {
            logger.debug('Checking route: %s', r.routeNumber, r.services);
            Object.keys(r.services || {}).forEach(tripId => {
                logger.debug('Checking route: %s, trip: %s', r.routeNumber, tripId, r.services[tripId]?.passingTimes);
                try {
                    if (r.services[tripId]?.passingTimes?.length) {
                        const ptIdx = r.services[tripId].passingTimes.findIndex(pt => {
                            let arrivalTime = dayjs(date).startOf('d').add(pt.arriveSecs, 's');
                            if (arrivalTime.isBefore(today)) {
                                arrivalTime = arrivalTime.add(1, 'd');
                            }
                            let departureTime = dayjs(date).startOf('d').add(pt.departSecs, 's');
                            if (departureTime.isBefore(today)) {
                                departureTime = departureTime.add(1, 'd');
                            }
                            if (school) {
                                const windowEnd = time + windowSecs;
                                logger.debug('Checking arriveSecs %d < %d < %d,  departSecs %d < %d < %d', time, pt.arriveSecs, windowEnd, time, pt.departSecs, windowEnd);
                                return (time <= pt.arriveSecs && pt.arriveSecs <= windowEnd) || (time <= pt.departSecs && pt.departSecs <= windowEnd);
                            }
                            logger.debug('Looking for %s between %s and %s on %s, school: %s Schedules: %s', arrivalTime.format('lll'), windowStart.format('lll'), windowEnd.format('lll'), date?.format('DD/M/YYYY'), school, allSchedules);
                            return arrivalTime.isBetween(windowStart, windowEnd, 's', '[]') || departureTime.isBetween(windowStart, windowEnd, 's', '[]');
                        });
                        if (ptIdx > -1) {
                            logger.debug('We have a pt. Check schedule: %s', (allSchedules && date));
                            let scheduleIds = r.services[tripId].scheduleIds;
                            if (allSchedules && date) {
                                logger.debug('Checking schedule...');
                                const days = Math.abs(windowStart.clone().startOf('d').diff(windowEnd.clone().startOf('d'), 'd'));
                                scheduleIds = r.services[tripId].scheduleIds
                                    .map(sId => allSchedules[sId])
                                    .filter(schedule => {
                                        if (!schedule || schedule.isObsolete(date)) return false;
                                        for (let i = 0; i <= days; i++) {
                                            if (schedule.isActive(windowStart.clone().add(i, 'd'), !school)) {
                                                return true;
                                            }
                                        }
                                        return false;
                                    })
                                    .map(schedule => schedule.scheduleId);
                                logger.debug('Filtered schedules', scheduleIds.map(sId => allSchedules[sId].scheduleName));
                            }
                            if (scheduleIds?.length) {
                                logger.debug('We have %d schedules', scheduleIds.length);
                                passingTimes.push({
                                    tripId,
                                    passingTime: r.services[tripId].passingTimes[ptIdx],
                                    routeId: r.routeId,
                                    route: r,
                                    scheduleIds
                                });
                            }
                        }
                    }
                } catch (e) {
                    logger.warn('Passing times for stop: %s on route %s: trip: %s', this.stopName, r.routeNumber, tripId, r.services[tripId]);
                    logger.warn(e);
                }
            });
        });
        return passingTimes;
    }

    createTransfers({
                        transferWindow,
                        defaultWindow,
                        arriveSecs,
                        fromStopId,
                        fromStopTimeId,
                        dropOffService,
                        waypoints,
                        distance,
                        duration,
                        routeFilterFn = () => true
                    } = {}) {
        if (!transferWindow && !this.transferWindow && !defaultWindow) {
            console.log('No window allocated for stop');
            return [];
        }
        const allServices = flatten(this.routes.map(route => {
            return values(route.services).map(trip => {
                trip.routeId = route.routeId;
                return trip;
            });
        })).filter(trip => routeFilterFn(trip.routeId, trip.tripId));

        if (!allServices.length) {
            console.log('No services found for stop');
            return [];
        }
        const transferWindowInSecs = (transferWindow || this.transferWindow || defaultWindow) * 60;
        const transfers = [];
        allServices.filter(potentialPickupService => potentialPickupService.routeId !== dropOffService.routeId && potentialPickupService.tripId !== dropOffService.tripId)
            .forEach(potentialPickupService => {
                // For each stop in the potentialPickupService, check if the passenger dwell time is within the transfer window
                const pickupStops = potentialPickupService.passingTimes.filter(potentialPassingTime => {
                    return potentialPassingTime.stopId === this.stopId;
                }).filter(pickupPassingTime => {
                    const passengerDwellTime = pickupPassingTime.departSecs - arriveSecs;
                    return passengerDwellTime >= 0 && passengerDwellTime <= transferWindowInSecs;
                });
                pickupStops.forEach(pickupStop => {
                    transfers.push({
                        transferId: ulid(),
                        fromRouteNumber: dropOffService.routeNumber,
                        fromRouteId: dropOffService.routeId,
                        fromTripId: dropOffService.tripId,
                        fromStopId,
                        fromStopTimeId,
                        toRouteNumber: potentialPickupService.routeNumber,
                        toRouteId: potentialPickupService.routeId,
                        toTripId: potentialPickupService.tripId,
                        toStopId: pickupStop.stopId,
                        toStopTimeId: pickupStop.stopTimeId,
                        waypoints, distance, duration,
                        arriveSecs, departSecs: pickupStop.departSecs
                    });
                });
            });
        return transfers;
    }

}

/**
 * Base stop is the stop that is used in the timetable. It is a stop that has a stopId, stopTimeId and is part of a trip.
 */
export class BaseStop extends Stop {
    constructor(data) {
        super(data);
        this.timingPoint = false;
        this.arriveSecs = 0;
        this.departSecs = 0;
        this.createdAt = 0;
        this.updatedAt = 0;
        Object.assign(this, data);
    }

    getDepartSecondsSinceMidnight() {
        return this.departSecs;
    }

    isInWindow(direction) {
        logger.debug('Checking %s in window for direction %s', this.stopName, direction);
        return direction === 'AM' ? this.isInStartWindow() : direction === 'PM' ? this.isInEndWindow() : true;
    }

    isInStartWindow() {
        logger.debug('Checking %s in start window', this.stopName, this.startBell, this.startBellWindow);
        if (!this.startBell || !this.startBellWindow) {
            return true;
        }
        const windowStart = this.startBell - this.startBellWindow;
        const windowEnd = this.startBell;
        const departTime = this.getDepartSecondsSinceMidnight();
        logger.debug('%d < %d > %d', windowStart, departTime, windowEnd);

        return departTime >= windowStart && departTime <= windowEnd;
    }

    isInEndWindow() {
        logger.debug('Checking %s in end window', this.stopName, this.endBell, this.endBellWindow);
        if (!this.endBell || !this.endBellWindow) {
            return true;
        }
        const windowStart = this.endBell;
        const windowEnd = this.endBell + this.endBellWindow;
        const departTime = this.getDepartSecondsSinceMidnight();
        logger.debug('%d < %d > %d', windowStart, departTime, windowEnd);

        return departTime >= windowStart && departTime <= windowEnd;
    }

    static isEqual(s1, s2) {
        return s1.stopId === s2.stopId && s1.stopTimeId === s2.stopTimeId;
    }
}

export class LinkedStop {
    constructor(data) {
        this.stopId = null;
        this.stop = null;
        this.distance = -1;
        this.duration = -1;
        this.waypoints = null;
        Object.assign(this, data);
    }

    static SERIALISED_KEYS() {
        return ['stopId', 'distance', 'duration', 'waypoints'];
    }
}

export class StopTime {
    constructor(data) {
        this.stop = null;
        this.timingPoint = false;
        this.arriveSecs = 0;
        this.departSecs = 0;
        this.createdAt = 0;
        this.updatedAt = 0;
        this.distance = 0; // Distance in metres from the start of the route

        Object.assign(this, data);
    }
}


export class Interchange extends Stop {
    constructor(data) {
        super(data);
        this.stops = [];
        Object.assign(this, data);
        this.interchange = true;
    }
}

export class Trip extends Model {

    static normaliseDate(trip) {
        if(!trip) return;
        if(trip.date) {
            trip.date = normaliseDate(trip.date)?.startOf('day') || null;
        }
        if (trip.dates) {
            trip.dates = trip.dates.map(d => normaliseDate(d)?.startOf('day') || null);
        }
        trip.stopTimes = trip.stopTimes?.map(st => ({...st, date: normaliseDate(st.date)?.startOf('day') || trip.dates?.[0]}));
    }

    constructor(data) {
        super();
        this.tripId = null;
        this.routeId = null;
        this.routeType = null;
        this.route = null;
        this.startStopIdx = -1;
        this.endStopIdx = -1;
        this.stopTimes = [];
        this.stops = [];
        this.scheduleIds = [];
        this.date = null;
        this.dates = [];
        this.driverId = null;
        this.vehicleId = null;
        this.shifts = [];
        this.headsign = null;
        this.transferAlias = null;
        this.charter = false;
        Object.assign(this, data);
        this.tripId = this.tripId || ulid();
        // this.stopTimes.forEach(st => {
        //     st.stopTimeId = st.stopTimeId || ulid()
        // });
        //TODO: Ensure the first stop is a public stop
        this.startTimeAsSecsSinceMidnight = getDepartureTimeAsSecondsSinceMidnight(this, this.stopTimes[0]);

        Trip.normaliseDate(this);
    }

    isSameTrip(trip) {
        // Check if both trips have the same number of stop times
        if (!this.stopTimes?.length || !trip.stopTimes?.length || this.stopTimes.length !== trip.stopTimes.length) {
            return false;
        }

        // Compare each stop time's arriveSecs and departSecs
        return this.stopTimes.every((stopTime, index) => {
            const otherStopTime = trip.stopTimes[index];
            return stopTime.arriveSecs === otherStopTime.arriveSecs && 
                   stopTime.departSecs === otherStopTime.departSecs;
        });
    }

    includes(trip, schedulesById) {
        if(this.date && trip.date) {
            return this.date.isSame(trip.date, 'day');
        }
        if (this.dates?.length && trip.dates?.length) {
            return trip.dates.some(d => this.dates.some(d2 => d.isSame(d2, 'day')));
        }
    }

    getRunningDates(schedulesById, sameOrAfter = dayjs()) {
        const dates = this.dates ? [...this.dates] : [];
        if(this.date) {
            dates.push(this.date);
        }
        this.scheduleIds.map(sId => schedulesById[sId]).forEach(schedule => {
            if (schedule) {
                dates.push(...schedule.getRunningDates(sameOrAfter));
            }
        });
        return dates;
    }

    isRunning(date = dayjs(), schedulesById) {
        return (this.dates || []).some(d => d.isSame(date, 'day')) || 
        (this.date && date.clone().startOf('day').isSame(this.date, 'day')) ||
        this.scheduleIds.some(sId => {
            const schedule = schedulesById[sId];
            return schedule && schedule.isActive(date);
        });
    }

    hasObsoleteCalendar(schedulesById, date = dayjs()) {
        return this.scheduleIds.some(sId => !schedulesById[sId] || schedulesById[sId].isObsolete(date));
    }


    getWpIndexForStopIdx(stopIdx) {
        return this.route?.getWpIndexForStopTimeId(this.stopTimes[stopIdx].stopTimeId);
    }

    getStopSequence(stopTime, allStops) {
        return this.getPublicStopTimes(allStops).findIndex(st => st.stopTimeId === stopTime.stopTimeId) + 1;
    }

    getTimingPointStops() {
        return this.stops.filter(timingPtFilter);
    }

    getPublicStopTimes(allStops) {
        return this.stopTimes.filter(st => publicStopFilter(allStops[st.stopId]));
    }

    getDriverRequirements() {
        const duration = this.getDuration();
        const dates = this.getRunningDates();
        const startDate = this.getDepartureTimeAsDayjs(this.stopTimes[0]);
        return {startTime: startDate.format('HH:mm'), dates, duration}
    }

    getDuration({startStopIdx = 0, endStopIdx = this.stopTimes.length - 1, stops = []} = {}) {
        let publicStopTimes = this.stopTimes;
        if(stops.length) {
            publicStopTimes = this.getPublicStopTimes(keyBy(stops, 'stopId'));
        }
        return getDepartureTimeAsDayjs(this, publicStopTimes[endStopIdx]).diff(getDepartureTimeAsDayjs(this, publicStopTimes[startStopIdx]), 's');
    }

    getTotalDuration() {
        return (this.getDuration() || 0) + (this.route?.startDepotDuration || 0) + (this.route?.endDepotDuration || 0) + (this.route?.returnDuration || 0);
    }

    getVisiblePublicStopTimes(allStops) {
        if (this.startStopIdx > -1 && this.endStopIdx > this.startStopIdx) {
            return this.getPublicStopTimes(allStops).slice(this.startStopIdx, this.endStopIdx + 1);
        }
        return [];
    }

    getArrivalTimeAsDayjs(stop) {
        return getArrivalTimeAsDayjs(this, stop, null, this.date || this.dates?.[0]);
    }

    getDepartureTimeAsDayjs(stop) {
        return getDepartureTimeAsDayjs(this, stop, null, this.date || this.dates?.[0]);
    }

    setStartTime(newStartSecsSinceMidnight) {
        if (this.stopTimes?.length) {
            const diff = newStartSecsSinceMidnight - this.stopTimes[0].departSecs;
            this.stopTimes?.forEach(st => {
                st.arriveSecs += diff;
                st.departSecs += diff;
            });
            this.stops?.forEach(st => {
                st.arriveSecs += diff;
                st.departSecs += diff;
            });
        }
    }

    getEndTime(showSeconds = false) {
        return this.stopTimes?.length ? getDepartureTimeAsDayjs(this, last(this.stopTimes)).format(showSeconds ? 'HH:mm:ss' : 'HH:mm') : showSeconds ? '--:--:--' : '--:--';
    }

    getStartTime(showSeconds = false) {
        return this.stopTimes?.length ? getDepartureTimeAsDayjs(this, this.stopTimes[0]).format(showSeconds ? 'HH:mm:ss' : 'HH:mm') : showSeconds ? '--:--:--' : '--:--';
    }

    getStartTimeAsDayJs(startStopIdx = 0, date = this.date || this.dates?.[0] || dayjs()) {
        return getDepartureTimeAsDayjs(this, this.stopTimes[startStopIdx], null, date);
    }

    getStartTimeAsSecondsSinceMidnight(startStopIdx = 0) {
        return getDepartureTimeAsSecondsSinceMidnight(this, this.stopTimes[startStopIdx]);
    }

    getEndTimeAsSecondsSinceMidnight(endStopIdx = this.stopTimes.length - 1) {
        if (endStopIdx >= this.stopTimes.length) {
            endStopIdx = this.stopTimes.length - 1;
        }
        return getArrivalTimeAsSecondsSinceMidnight(this, this.stopTimes[endStopIdx]);
    }

    getStopTime(stop) {
        return this.stopTimes.filter(st => st.stopTimeId === stop.stopTimeId)[0] || null;
    }

    isStopTimeInWindow(stopTime, time, window) {
        const departureTimeAtStopTime = getDepartureTimeAsSecondsSinceMidnight(this, stopTime);
        return departureTimeAtStopTime >= time && departureTimeAtStopTime <= time + window;
    }

    getAllStopTimes(stop) {
        if (!stop) {
            return [];
        }
        return this.stopTimes.reduce((result, st, i) => {
            if (st.stopId === stop.stopId)
                result.push(st);
            return result;
        }, []);
    }

    clone() {
        return new Trip({...this, stopTimes: [...this.stopTimes]});
    }

    addSchedule(scheduleId) {
        this.scheduleIds.push(scheduleId);
    }

    removeSchedule(scheduleId) {
        this.scheduleIds = this.scheduleIds.filter(sId => sId !== scheduleId);
    }

    getRouteType() {
        return this.routeType || this.route?.routeType || 'Regular';
    }

}

export class TripShift extends Model {
    constructor(data) {
        super();
        this.tripShiftId = null;
        this.tripId = null;
        this.vehicleIds = [];
        this.driverIds = [];
        Object.assign(this, data);
        this.tripShiftId = this.tripShiftId || ulid();
    }
}
